import type { ArticleVisual } from "@/lib/article-visual";
/**
 * Zentrale Website-Konfiguration & Inhalte (Single Source of Truth).
 * Firmendaten, Navigation, Services und SEO-Grunddaten liegen hier gebündelt,
 * damit Texte an einer Stelle gepflegt werden können.
 */

import type { IconName } from "@/components/ui/Icon";

/**
 * Die Rufnummer steht genau EINMAL hier – alle anderen Formen werden daraus
 * berechnet.
 *
 * Vorher waren Anzeige und tel:-Link zwei getrennte Zeichenketten, und im
 * Link war eine Ziffer zu viel: angezeigt wurde die richtige Nummer, gewählt
 * eine, die es nicht gibt. Das betraf jeden „Anrufen"-Knopf, Impressum,
 * Datenschutz und Footer – und fiel niemandem auf, weil die Seite ja die
 * richtige Nummer ZEIGTE. Zwei Quellen für dieselbe Zahl laufen irgendwann
 * auseinander, darum hier nur noch eine.
 *
 * Angezeigt wird die österreichische Schreibweise. Gewählt und an Google
 * gemeldet wird die internationale: areaServed umfasst AT, DE und CH, und
 * aus Deutschland oder der Schweiz lässt sich eine 0660 nicht anrufen.
 */
const TELEFON_NATIONAL = "0660 28 56 985";
const TELEFON_INTERNATIONAL = `+43 ${TELEFON_NATIONAL.replace(/^0/, "")}`;

export const site = {
  name: "WeDev-IT",
  legalName: "WeDev-IT GmbH",
  tagline: "We Develop IT",
  claim: "Maßgeschneiderte Software, IT-Consulting & digitales Wachstum – KI-gestützt zum Bruchteil der üblichen Kosten.",
  /*
   * Kanonische Produktions-Domain – OHNE www.
   * Caddy leitet www.wedev-it.at dauerhaft (301) hierher weiter, deshalb muss
   * jedes Selbstverweis-Signal (Canonical, Sitemap, robots Host, JSON-LD @id)
   * auf diese Variante zeigen. Zeigte es auf www, verwiese die Seite auf eine
   * Adresse, die sofort weiterleitet – ein widersprüchliches Signal für Google.
   */
  url: "https://wedev-it.at",
  locale: "de-AT",
  founded: 2024,
  contact: {
    email: "office@wedev-it.at",
    /** Für Menschen: 0660 28 56 985. */
    phone: TELEFON_NATIONAL,
    /** Für Maschinen und fürs Ausland: +43 660 28 56 985. */
    phoneIntl: TELEFON_INTERNATIONAL,
    phoneHref: `tel:${TELEFON_INTERNATIONAL.replace(/[^\d+]/g, "")}`,
    street: "Opernring 1/R745-748",
    zip: "1010",
    city: "Wien",
    country: "Österreich",
    countryCode: "AT",
    geo: { lat: 48.2028, lng: 16.3690 },
  },
  founder: {
    name: "Ing. Özgür Deli, MSc CMSE",
    role: "Geschäftsführer & Gründer",
    note: "Gastlektor · 15+ Jahre Enterprise-Projekterfahrung",
  },
  social: {
    linkedin: "https://www.linkedin.com/company/wedev-it",
  },
  /**
   * Google-Partner-Status. Bleibt `undefined`, solange der Nachweis nicht
   * vorliegt – dann zeigt `GooglePartnerBadge` nichts an.
   *
   * Zum Aktivieren: Abzeichen-Grafik aus dem eigenen Partners-Konto als
   * `public/brand/google-partner.png` ablegen und hier die Adresse des
   * öffentlichen Partnerprofils eintragen, z. B.
   *   googlePartner: { profilUrl: "https://www.google.com/partners/agency?id=…" },
   *
   * Nicht aktivieren, ohne dass der Status im Partners-Konto tatsächlich als
   * aktiv ausgewiesen ist: Das Abzeichen ohne Status ist irreführende Werbung
   * (UWG) und eine Markenverletzung gegenüber Google. Der Status kann
   * erlöschen, deshalb gehört er hierher und nicht fest in eine Komponente.
   */
  googlePartner: undefined as { profilUrl: string } | undefined,

  /**
   * Öffentliches Google-Unternehmensprofil. Ist es gesetzt, verlinkt die
   * Sternreihe (GoogleBewertung) dorthin – dann lässt sich die Bewertung
   * mit zwei Klicks nachprüfen, statt sie glauben zu müssen.
   */
  googleProfilUrl: "" as string,

  /** Auszeichnung – wird im Hero jeder Seite als USP gezeigt (siehe KurierBadge). */
  award: {
    name: "Kurier Gütesiegel Topberater",
    year: 2026,
    url: "https://kurier.at/thema/guetesiegel-imwf-und-kurier-die-top-berater-des-landes/403152243",
  },
  stats: [
    { value: "15+", label: "Jahre Enterprise-Erfahrung" },
    { value: "100+", label: "zufriedene Kunden" },
    { value: "70 %", label: "günstiger durch KI-gestützte Entwicklung" },
    { value: "100 %", label: "Fokus auf Qualität & Sicherheit" },
  ],
} as const;

/**
 * Datum der letzten inhaltlichen Überarbeitung (ISO, YYYY-MM-DD).
 * Fließt in `lastModified` der Sitemap. Bitte nur anpassen, wenn Inhalte
 * tatsächlich überarbeitet wurden – nicht bei jedem Deployment.
 */
export const lastContentUpdate = "2026-08-24";

export type NavItem = { label: string; href: string; description?: string };

export const mainNav: NavItem[] = [
  { label: "Leistungen", href: "/leistungen" },
  { label: "NIS2", href: "/nis2-beratung" },
  { label: "Branchen", href: "/branchen" },
  { label: "Lösungen", href: "/loesungen" },
  { label: "Workshops", href: "/workshops" },
  { label: "Websites", href: "/websites" },
  { label: "Referenzen", href: "/referenzen" },
  { label: "Ratgeber", href: "/ratgeber" },
  { label: "Ablauf", href: "/#ablauf" },
  { label: "Über uns", href: "/ueber-uns" },
  { label: "FAQ", href: "/#faq" },
  { label: "Kontakt", href: "/kontakt" },
];

/**
 * Angebote mit eigener Preis- bzw. Ergebnisseite. Stehen im Mega-Menü als
 * eigene kurze Gruppe über den Workshops – so bleiben sie auffindbar, ohne
 * die Leistungsspalte zu verlängern.
 */
export const angebote: { href: string; label: string; hint: string; icon: IconName }[] = [
  { href: "/it-betreuung", label: "IT-Betreuung", hint: "Fixpreis ab 99 € im Monat", icon: "shield-check" },
  { href: "/grossauftrag-system", label: "Großaufträge für Handwerker", hint: "Garantie: sonst kostenlos weiter", icon: "hammer" },
  { href: "/kundengewinnung", label: "Performance Marketing", hint: "Google Ads, Analytics & Tag Manager", icon: "target" },
  { href: "/google-ads-betreuung", label: "Google Ads Betreuung", hint: "Laufend ab 390 € im Monat", icon: "target" },
  { href: "/seo-betreuung", label: "SEO Betreuung", hint: "Laufend ab 690 € im Monat", icon: "search" },
  { href: "/google-unternehmensprofil", label: "Google-Unternehmensprofil", hint: "In Maps gefunden werden", icon: "map-pin" },
  { href: "/ki-bedarfsanalyse", label: "KI-Bedarfsanalyse", hint: "Festpreis, 90 Minuten mit Auswertung", icon: "sparkles" },
  { href: "/nis2-beratung", label: "NIS2-Beratung", hint: "NISG 2026 prüfbar umsetzen", icon: "lock" },
];

/**
 * Kostenlose Einstiege. Bewusst von `angebote` getrennt: Sie kosten nichts,
 * und wer sie zwischen Preisangaben stellt, nimmt ihnen genau das, was sie
 * wirksam macht – die niedrige Hürde.
 */
export const einstiege: { href: string; label: string; hint: string; icon: IconName }[] = [
  { href: "/google-ads-check", label: "Google Ads Check", hint: "Konto geprüft, schriftlich", icon: "chart" },
  { href: "/seo-check", label: "SEO-Check", hint: "Website geprüft, schriftlich", icon: "compass" },
  { href: "/nis2-check", label: "NIS2-Betroffenheit", hint: "In 3 Minuten geprüft", icon: "lock" },
];

export const legalNav: NavItem[] = [
  { label: "Impressum", href: "/impressum" },
  { label: "Datenschutz", href: "/datenschutz" },
  { label: "Cookie-Richtlinie", href: "/cookie-richtlinie" },
  { label: "AGB", href: "/agb" },
];

/**
 * Leistungen inkl. eigener SEO-Landingpage (/leistungen/[slug]).
 * `keywords` fließen in Meta-Daten & Content-Struktur ein (Long-Tail-SEO).
 */
export type Service = {
  slug: string;
  icon: IconName;
  /**
   * Kernleistung – steht vorne und bekommt das Gewicht.
   *
   * Vorher standen alle acht Leistungen gleichrangig nebeneinander, in einem
   * Raster aus acht gleich aussehenden Kacheln. Das liest sich als „wir
   * machen alles“, und „alles“ ist kein Angebot: Wer überall mitbietet,
   * bietet gegen Spezialisten und gewinnt nur über den Preis.
   *
   * Entscheidung von Özgür am 04.11.2026: vorne Softwareentwicklung &
   * Webdesign, SEO und Google Ads – das ist der Ablauf, an dem die
   * belegbare Ergebniszahl hängt (siehe lib/kundengewinnung.ts).
   *
   * Die übrigen bleiben vollständig erhalten, mit eigener Seite und eigenen
   * Suchbegriffen. Sie verschwinden nur aus der ersten Reihe. Löschen wäre
   * der teuerste Fehler: Diese Seiten bringen Long-Tail-Besucher, und
   * Platzierungen zurückzuholen dauert Monate.
   */
  kern?: true;
  title: string;
  navTitle: string;
  tagline: string;
  summary: string;
  keywords: string[];
  metaTitle: string;
  metaDescription: string;
  heroHeadline: string;
  heroSub: string;
  benefits: { title: string; text: string }[];
  /**
   * Grafik nach den Vorteilen – dort, wo der Leser abwägt, ob das zu ihm
   * passt. Dieselben Bausteine wie im Ratgeber (lib/article-visual.ts).
   *
   * Nur `compare` und `matrix`, solange es keine belegten Zahlen gibt:
   * Beide stellen gegenüber, ohne eine Zahl zu behaupten.
   */
  visual?: ArticleVisual;
  deliverables: string[];
  // Ausführliche Content-Blöcke für SEO-Volltext.
  sections: { heading: string; body: string }[];
  faq: { q: string; a: string }[];
  /**
   * Verweise auf echte Projekte und passende Beiträge. Wichtig für die
   * umkämpften Kopf-Suchbegriffe: Sie zeigen Nachweise statt Versprechen und
   * verteilen die interne Verlinkung auf die Belege.
   */
  weiterfuehrend?: { label: string; href: string }[];
  /**
   * Slugs aus lib/websites: echte, gebaute Kundenseiten als Bildbeleg.
   * Nur eintragen, wo die Seite zur Leistung wirklich etwas beweist – ein
   * hingebogener Beleg ist schlechter als keiner.
   */
  belege?: string[];
  example: {
    title: string;
    persona: string;
    problem: string;
    solution: string;
    result: string;
  };
};

export const services: Service[] = [
  {
    slug: "softwareentwicklung",
    kern: true,
    icon: "code",
    title: "Softwareentwicklung",
    navTitle: "Softwareentwicklung",
    tagline: "Individuelle Software, die genau zu Ihrem Geschäft passt.",
    summary:
      "Maßgeschneiderte Web- und Softwarelösungen, Web-Apps und Automatisierungen – sauber gebaut, getestet und wartbar. KI-gestützt entwickeln wir schneller und dadurch deutlich günstiger.",
    keywords: [
      "Softwareentwicklung Wien",
      "Softwarefirma Wien",
      "Software entwickeln lassen Wien",
      "Individualsoftware Wien",
      "individuelle Softwareentwicklung Österreich",
      "maßgeschneiderte Software für KMU",
      "Webanwendung entwickeln lassen",
      "KI-gestützte Softwareentwicklung",
    ],
    metaTitle: "Softwareentwicklung Wien für KMU",
    metaDescription:
      "Individuelle Software aus Wien: Web-Apps, Automatisierung, Schnittstellen. KI-gestützt, bis zu 70 % günstiger. Jetzt kostenloses Erstgespräch.",
    heroHeadline: "Individuelle Software, die Ihr Unternehmen schneller macht",
    heroSub:
      "Von der Idee bis zum Go-Live: Wir entwickeln maßgeschneiderte Software, die manuelle Arbeit ersetzt, Fehler reduziert und mit Ihnen wächst – KI-gestützt und dadurch zum Bruchteil der üblichen Kosten.",
    benefits: [
      { title: "Auf Ihr Geschäft zugeschnitten", text: "Keine Kompromisse durch Standardsoftware. Sie bekommen genau die Funktionen, die Ihre Prozesse brauchen." },
      { title: "Bis zu 70 % günstiger", text: "Durch KI-gestützte Entwicklung liefern wir in einem Bruchteil der Zeit – das sehen Sie direkt am Angebot." },
      { title: "Sauber & wartbar", text: "Getesteter, dokumentierter Code. Sie sind nie von einer einzigen Person abhängig." },
    ],
    visual: {
      kind: "matrix",
      caption: "Wann sich eigene Software rechnet",
      good: {
        title: "Spricht dafür",
        items: [
          "Der Ablauf ist Ihr Wettbewerbsvorteil",
          "Lizenzkosten wachsen mit jedem neuen Mitarbeiter",
          "Mehrere Werkzeuge werden täglich von Hand verbunden",
          "Standardsoftware zwingt den Betrieb in fremde Abläufe",
          "Die Daten sollen im eigenen Haus bleiben",
        ],
      },
      bad: {
        title: "Spricht dagegen",
        items: [
          "Es gibt ein Standardprodukt, das passt",
          "Der Ablauf ändert sich gerade ohnehin grundlegend",
          "Niemand im Betrieb kann das Vorhaben fachlich begleiten",
          "Es geht um Buchhaltung, Lohn oder andere geregelte Standardaufgaben",
        ],
      },
      note: "Wir sagen im Erstgespräch auch ab. Eine eigene Lösung für etwas zu bauen, das es fertig gibt, ist für beide Seiten das schlechtere Geschäft.",
    },
    deliverables: [
      "Web-Apps & Kundenportale",
      "Prozess-Automatisierung & Schnittstellen (APIs)",
      "Datenbanken & Dashboards",
      "Ablösung von Excel-Chaos & Insellösungen",
      "Wartung, Support & Weiterentwicklung",
    ],
    sections: [
      {
        heading: "Wann sich individuelle Softwareentwicklung lohnt",
        body:
          "Sobald Ihr Team wiederkehrende Aufgaben manuell in Excel-Listen, E-Mails oder mehreren getrennten Tools erledigt, kostet das jeden Tag Zeit und Nerven – und produziert Fehler. Individuelle Software bündelt genau diese Abläufe in einem System, das so funktioniert, wie Ihr Unternehmen wirklich arbeitet. Anders als Standardsoftware zwingt sie Sie nicht in fremde Prozesse, sondern bildet Ihre ab.",
      },
      {
        heading: "Wie wir mit KI schneller und günstiger entwickeln",
        body:
          "Wir setzen KI in jedem Schritt ein – bei Konzeption, Code, Tests und Dokumentation. Das beschleunigt die Entwicklung massiv, ohne die Qualität zu senken: Jede Zeile wird geprüft, getestet und sauber dokumentiert. Für Sie bedeutet das ein Angebot, das oft deutlich unter dem liegt, was klassische Agenturen verlangen – bei mindestens gleichwertigem Ergebnis.",
      },
      {
        heading: "Technologien & Vorgehen",
        body:
          "Wir arbeiten mit modernen, langfristig gepflegten Technologien (u. a. TypeScript, React/Next.js, Node.js, Python, SQL/PostgreSQL) und liefern in kurzen, transparenten Etappen. Sie sehen früh erste Ergebnisse, geben laufend Feedback und behalten die volle Kontrolle über Budget und Umfang. Qualitätssicherung ist bei uns kein Nachgedanke, sondern von der ersten Zeile an eingebaut.",
      },
      {
        heading: "Was individuelle Software kostet – und wovon es abhängt",
        body:
          "Die ehrliche Antwort lautet: Es hängt vom Umfang ab, und wer ohne Gespräch eine Zahl nennt, rät. Trotzdem lässt sich der Rahmen einordnen. Den größten Einfluss hat die Anzahl der Abläufe, die abgebildet werden sollen – ein System für Angebote und Aufträge ist überschaubar, eines mit zusätzlicher Zeiterfassung, Lagerlogik und Schnittstellen zur Buchhaltung deutlich umfangreicher. Danach kommen Schnittstellen zu Fremdsystemen: Sie sind selten der große Brocken, kosten aber Zeit, weil jede Gegenstelle anders funktioniert. Und schließlich die Anzahl der Rollen, denn jede zusätzliche Sicht auf dieselben Daten bedeutet eigene Ansichten und Berechtigungen. Wir liefern deshalb keinen Stundensatz ins Blaue, sondern nach dem Erstgespräch einen Fixpreis-Rahmen für einen klar abgegrenzten ersten Ausbauschritt. Sie wissen dann, was Sie bekommen und was es kostet, bevor die erste Zeile geschrieben wird.",
      },
      {
        heading: "Wem die Software gehört und was passiert, wenn wir uns trennen",
        body:
          "Diese Frage stellen zu wenige Auftraggeber, und sie ist die wichtigste. Bei uns gilt: Sie erhalten den vollständigen Quellcode samt Dokumentation, und zwar nicht erst am Ende, sondern laufend. Der Code liegt in einem Repository, auf das Sie Zugriff haben. Die Datenbank gehört Ihnen, ein Export ist jederzeit möglich. Wir setzen auf verbreitete Technologien, damit jeder andere Entwickler weiterarbeiten kann, ohne sich in eine Eigenkonstruktion einarbeiten zu müssen. Sollten Sie mit uns nicht zufrieden sein, nehmen Sie Ihr System mit. Das klingt nach einem Nachteil für uns – tatsächlich ist es der beste Grund, gute Arbeit zu liefern: Wir behalten Kunden, weil sie bleiben wollen, nicht weil sie festsitzen.",
      },
      {
        heading: "Woran Softwareprojekte scheitern – und wie wir gegensteuern",
        body:
          "Die häufigste Ursache ist nicht Technik, sondern ein zu großer erster Schritt. Wenn zwölf Monate lang gebaut wird, bevor jemand das Ergebnis benutzt, hat sich das Geschäft in der Zwischenzeit verändert und niemand hat es gemerkt. Die zweite Ursache ist eine Anforderungsliste, die aus Wunschdenken statt aus Beobachtung entstanden ist: Funktionen, die jemand für nötig hielt, die aber im Alltag niemand öffnet. Die dritte ist fehlende Beteiligung derer, die täglich damit arbeiten sollen. Wir arbeiten deshalb in kurzen Etappen mit nutzbaren Zwischenständen, beginnen mit den drei bis fünf Abläufen, die messbar Zeit fressen, und binden die Menschen ein, die das System später bedienen. Was sich als überflüssig herausstellt, bauen wir nicht zu Ende – das spart mehr Geld als jede Rabattverhandlung.",
      },
      {
        heading: "Softwareentwicklung in Wien – was das praktisch bedeutet",
        body:
          "Unser Sitz ist am Opernring im 1. Bezirk, und ein Teil unserer Arbeit lässt sich nur vor Ort machen: zusehen, wie im Betrieb tatsächlich gearbeitet wird, mit den Leuten sprechen, die das System später bedienen, und zum Go-Live daneben stehen. Im Raum Wien geht das ohne Reisetag, und genau deshalb arbeiten wir hier besonders gern. Alles andere – Entwicklung, Abstimmungen, Übergaben – läuft ohnehin verteilt, weshalb wir Betriebe in ganz Österreich, Deutschland und der Schweiz betreuen. Ortsbezug heißt bei uns außerdem: Verträge nach österreichischem Recht, Rechnungen mit österreichischer USt, Hosting in der EU und Datenschutz nach DSGVO, geprüft am konkreten Fall. Bei Förderungen für Digitalisierungsvorhaben geben wir Hinweise und liefern die Unterlagen, die Förderstellen üblicherweise sehen wollen: klar abgegrenzter Umfang, nachvollziehbares Angebot, saubere Dokumentation.",
      },
      {
        heading: "Betrieb, Hosting und Sicherheit",
        body:
          "Software ist mit dem Go-Live nicht fertig, sie fängt dort an zu arbeiten. Wir betreiben Anwendungen auf Servern in der EU, mit getrennten Umgebungen für Test und Betrieb, täglicher Datensicherung samt geprüfter Wiederherstellung und überwachtem Zustand. Sicherheitsaktualisierungen spielen wir laufend ein, statt zu warten, bis etwas passiert. Wenn Sie eigene Server oder einen bestehenden Dienstleister haben, arbeiten wir dort hinein – das Hosting muss nicht bei uns liegen. Fällt Ihr Unternehmen unter das NISG 2026, achten wir darauf, dass die neue Anwendung zu den Anforderungen passt: Mehr-Faktor-Anmeldung, Protokollierung, Rollen und Löschkonzept.",
      },
      {
        heading: "Wie lange die Entwicklung dauert",
        body:
          "Für einen klar umrissenen ersten Ausbauschritt liegt die Spanne meist bei wenigen Wochen bis zu einigen Monaten. Entscheidend ist weniger unsere Geschwindigkeit als die Zahl der Entscheidungen, die währenddessen zu treffen sind. Projekte laufen dann schnell, wenn auf Ihrer Seite eine Person benannt ist, die verbindlich entscheiden darf, und wenn Rückmeldungen zeitnah kommen. Wir planen deshalb feste, kurze Abstimmungspunkte statt langer Funkstille mit großem Knall am Ende. Sie sehen früh, wohin es geht, und können gegensteuern, solange es billig ist.",
      },
    ],
    faq: [
      { q: "Was kostet die Entwicklung einer individuellen Software?", a: "Das hängt vom Umfang ab. Dank KI-gestützter Entwicklung liegen wir meist deutlich unter klassischen Agenturen. Im kostenlosen Erstgespräch bekommen Sie eine ehrliche Einschätzung und einen Fixpreis-Rahmen." },
      { q: "Bekomme ich den Quellcode?", a: "Ja. Sie erhalten den vollständigen, dokumentierten Quellcode und sind nie von einem einzelnen Anbieter abhängig." },
      { q: "Wie lange dauert ein Projekt?", a: "Erste nutzbare Versionen liefern wir oft in wenigen Wochen. Wir arbeiten in Etappen, damit Sie schnell Mehrwert sehen." },
      { q: "Können wir klein anfangen und später ausbauen?", a: "Ja, und wir empfehlen es sogar. Wir bauen den ersten Ausbauschritt so, dass er für sich funktioniert und Ihnen sofort Arbeit abnimmt. Erst wenn der sich bewährt hat, kommt der nächste dazu. Die Architektur legen wir von Anfang an so an, dass Erweiterungen keinen Neubau bedeuten." },
      { q: "Was passiert, wenn sich unsere Anforderungen mitten im Projekt ändern?", a: "Das ist der Normalfall, kein Störfall. Weil wir in kurzen Etappen liefern, betrifft eine Änderung meist nur die nächste Etappe, nicht das Gesamtwerk. Verschiebt sich der Umfang deutlich, sprechen wir offen über Auswirkungen auf Zeit und Preis, bevor gearbeitet wird – keine stillen Nachträge." },
      { q: "Was ist nach dem Go-Live?", a: "Wir überwachen den Betrieb, spielen Sicherheitsaktualisierungen ein und stehen für Fragen bereit. Ob Sie darüber hinaus Weiterentwicklung beauftragen, entscheiden Sie. Ein Wartungsvertrag ist möglich, aber keine Bedingung – die Software läuft auch ohne uns weiter." },
      { q: "Können Sie mit unserer bestehenden Software zusammenarbeiten?", a: "In den meisten Fällen ja. Viele Systeme bieten Schnittstellen; wo keine existiert, arbeiten wir mit Import- und Exportdateien. Was in Ihrem konkreten Fall möglich ist, prüfen wir vor dem Angebot – wir versprechen keine Anbindung, die wir nicht geprüft haben." },
      { q: "Arbeiten Sie nur in Wien?", a: "Unser Sitz ist in Wien, und im Raum Wien sind Termine vor Ort einfach möglich. Entwickelt und abgestimmt wird ohnehin verteilt, deshalb betreuen wir Kunden in ganz Österreich sowie in Deutschland und der Schweiz. Wo Anfahrt anfällt, steht sie vorher im Angebot." },
      { q: "Wo liegen unsere Daten?", a: "Auf Servern in der EU, mit täglicher Sicherung und geprüfter Wiederherstellung. Wenn Sie eigenes Hosting oder einen bestehenden Dienstleister haben, arbeiten wir dort hinein. Die Daten gehören Ihnen, ein vollständiger Export ist jederzeit möglich." },
      { q: "Können wir Sie erst für einen kleinen Auftrag testen?", a: "Gern. Viele beginnen mit einem klar abgegrenzten ersten Ausbauschritt zum Fixpreis. Sie sehen daran, wie wir arbeiten, bevor Sie größere Beträge freigeben – und behalten das Ergebnis samt Quellcode in jedem Fall." },
    ],
    weiterfuehrend: [
      { label: "EasyOffer: Angebots- und Rechnungsplattform mit KI-Assistent", href: "/referenzen/easyoffer" },
      { label: "ONYX Cuts: Excel durch eine eigene Web-App abgelöst", href: "/referenzen/onyx-cuts" },
      { label: "ORDERBASE: Bestellplattform für die Gastronomie", href: "/referenzen/orderbase" },
      { label: "Ratgeber: Was kostet individuelle Software?", href: "/ratgeber/was-kostet-individuelle-software" },
      { label: "Ratgeber: Excel ablösen – wann sich eigene Software rechnet", href: "/ratgeber/excel-abloesen-eigene-software" },
      { label: "Lösung: Excel ablösen", href: "/loesungen/excel-abloesen" },
    ],
    belege: ["easyoffer", "heimlichgut", "blacktrace"],
    example: {
      title: "Vom Excel-Chaos zum digitalen Auftragssystem",
      persona: "Handwerksbetrieb mit 12 Mitarbeitern",
      problem: "Aufträge, Termine und Materiallisten lagen verteilt in Excel, WhatsApp und Zetteln. Nichts war aktuell, Angebote dauerten Tage.",
      solution: "Wir haben ein zentrales Auftrags- und Angebotssystem gebaut – mit Kundenverwaltung, automatischen Angeboten und mobiler Ansicht für die Monteure.",
      result: "Angebote in Minuten statt Tagen, kein doppeltes Erfassen mehr, spürbar weniger Fehler – und ein Team, das wieder arbeiten statt suchen kann.",
    },
  },
  {
    slug: "it-consulting",
    icon: "compass",
    title: "IT-Consulting & Beratung",
    navTitle: "IT-Consulting",
    tagline: "Klarheit statt Buzzwords – die richtige Technologie-Entscheidung.",
    summary:
      "Unabhängige IT-Beratung für KMU: Wir bringen Ordnung in Ihre IT-Landschaft, wählen die passenden Tools und begleiten Digitalisierung so, dass sie sich rechnet.",
    keywords: [
      "IT-Consulting Wien",
      "IT-Beratung für KMU",
      "Digitalisierungsberatung Österreich",
      "unabhängige IT-Beratung",
      "IT-Strategie Mittelstand",
    ],
    metaTitle: "IT-Consulting Wien | Beratung für KMU",
    metaDescription:
      "Unabhängige IT-Beratung aus Wien: passende Tools, klare Prozesse, umsetzbarer Fahrplan statt Foliensatz. Jetzt kostenloses Erstgespräch.",
    heroHeadline: "IT-Entscheidungen, die sich rechnen – ohne Buzzword-Bingo",
    heroSub:
      "Wir beraten Sie unabhängig und verständlich: welche Software, welche Prozesse, welche Investitionen wirklich sinnvoll sind. Damit Sie Geld in die richtigen Dinge stecken.",
    benefits: [
      { title: "Herstellerunabhängig", text: "Wir verkaufen keine Lizenzen. Unsere Empfehlung richtet sich allein nach Ihrem Nutzen." },
      { title: "Verständlich erklärt", text: "Keine Fachbegriffe zum Selbstzweck. Sie verstehen jede Empfehlung und die Gründe dahinter." },
      { title: "Umsetzbar", text: "Sie bekommen keinen Foliensatz zum Ablegen, sondern einen konkreten, priorisierten Fahrplan." },
    ],
    visual: {
      kind: "matrix",
      caption: "Woran IT-Entscheidungen in KMU meist scheitern",
      good: {
        title: "Was wirklich hilft",
        items: [
          "Zuerst klären, welches Problem gelöst werden soll",
          "Zwei bis drei Wege mit Kosten über fünf Jahre",
          "Prüfen, wer die Daten am Ende besitzt",
          "Ausstieg mitdenken, bevor man einsteigt",
          "Eine Entscheidung treffen und sie dokumentieren",
        ],
      },
      bad: {
        title: "Was regelmäßig schiefgeht",
        items: [
          "Das Werkzeug steht fest, bevor das Problem beschrieben ist",
          "Entschieden wird nach Anschaffungspreis statt Gesamtkosten",
          "Der Anbieter berät über sein eigenes Produkt",
          "Niemand ist zuständig, also entscheidet niemand",
        ],
      },
      note: "Wir verkaufen keine Lizenzen und bekommen von keinem Hersteller Provision. Das ist der Grund, warum eine Empfehlung auch lauten kann, nichts zu kaufen.",
    },
    deliverables: [
      "IT- & Digitalisierungs-Check",
      "Tool- & Software-Auswahl",
      "Prozessanalyse & Automatisierungspotenziale",
      "Technische Konzepte & Architektur",
      "Begleitung bei der Umsetzung",
    ],
    sections: [
      {
        heading: "Für wen sich IT-Consulting lohnt",
        body:
          "Viele KMU haben über die Jahre eine gewachsene IT-Landschaft: hier ein Tool, dort eine Insellösung, dazwischen Excel. Das funktioniert – bis es teuer, unsicher oder unübersichtlich wird. Wir schauen mit Ihnen aufs große Ganze: Was können Sie zusammenführen, automatisieren oder weglassen? Wo entstehen Risiken? Und wo bringt eine Investition den größten Hebel?",
      },
      {
        heading: "Digitalisierung, die sich rechnet",
        body:
          "Digitalisierung ist kein Selbstzweck. Wir bewerten jeden Vorschlag danach, ob er Zeit spart, Fehler reduziert oder Umsatz bringt – und in welchem Verhältnis Aufwand und Nutzen stehen. So investieren Sie gezielt dort, wo es sich am schnellsten auszahlt, statt Budget in Prestigeprojekte zu stecken.",
      },
      {
        heading: "Fördermittel & Zuschüsse",
        body:
          "Für Digitalisierungs- und Software-Projekte gibt es in Österreich oft Förderungen. Wir weisen Sie auf passende Möglichkeiten hin und berücksichtigen sie in der Planung, damit Sie kein Geld liegen lassen.",
      },
      {
        heading: "Was ein IT-Check konkret liefert",
        body:
          "Am Anfang steht eine Bestandsaufnahme: Welche Systeme sind im Einsatz, wer nutzt sie, welche Daten fließen zwischen ihnen, und wo wird von Hand übertragen, was eigentlich automatisch gehen müsste. Wir sprechen dabei nicht nur mit der Geschäftsführung, sondern mit den Menschen, die täglich damit arbeiten – dort zeigen sich die Umwege, die in keinem Organigramm stehen. Das Ergebnis ist kein Foliensatz zum Ablegen, sondern eine priorisierte Liste: Was bringt viel und kostet wenig, was ist ein Risiko und muss ohnehin gelöst werden, was kann warten. Jeder Punkt mit geschätztem Aufwand und erwartetem Nutzen, damit Sie selbst entscheiden können und nicht auf unser Wort angewiesen sind.",
      },
      {
        heading: "Wann sich Standardsoftware lohnt – und wann nicht",
        body:
          "Wir verdienen an Eigenentwicklung, empfehlen sie aber oft nicht. Standardsoftware ist die bessere Wahl, wenn Ihr Ablauf dem entspricht, was die Branche üblicherweise tut: Buchhaltung, Lohnverrechnung, Warenwirtschaft im Handel. Diese Bereiche sind durchreguliert, ändern sich mit dem Gesetzgeber, und niemand sollte das selbst pflegen. Eigenentwicklung lohnt sich dort, wo Ihr Vorgehen der Grund ist, warum Kunden zu Ihnen kommen – wenn Sie eine besondere Kalkulation haben, eine ungewöhnliche Konfiguration anbieten oder einen Ablauf, den kein Standardprodukt kennt. Dort zwingt Standardsoftware Sie in fremde Muster und macht Sie austauschbar. Diese Unterscheidung treffen wir im Erstgespräch offen, auch wenn das Ergebnis lautet, dass Sie kein Projekt mit uns brauchen.",
      },
      {
        heading: "Wie wir Aufwand und Nutzen bewerten",
        body:
          "Jeder Vorschlag bekommt eine einfache Rechnung: Wie viele Stunden kostet der aktuelle Zustand pro Monat, was würde die Änderung einmalig kosten, und ab wann ist sie eingespielt. Wir rechnen dabei bewusst konservativ und mit Ihren echten Stundensätzen, nicht mit Branchenmittelwerten. Manche Vorschläge fallen dabei durch – das ist der Sinn der Übung. Was übrig bleibt, hat den Test bestanden und lässt sich einem Steuerberater oder einer Bank erklären. Für Digitalisierungsvorhaben gibt es in Österreich zudem regelmäßig Förderungen; wir weisen auf passende Programme hin und planen so, dass die üblichen Anforderungen erfüllbar sind – die Antragstellung selbst bleibt bei Ihnen oder Ihrer Förderberatung.",
      },
      {
        heading: "Was Beratung ohne Produktverkauf bedeutet",
        body:
          "Wir sind kein Wiederverkäufer und bekommen von keinem Hersteller Provision. Das ist kein Werbesatz, sondern hat eine praktische Folge: Wenn eine kostenlose Lösung Ihr Problem löst, sagen wir das. Wenn Ihre bestehende Software besser ist als ihr Ruf und nur falsch eingerichtet wurde, sagen wir auch das. Beratung, die sich über Lizenzverkäufe finanziert, hat diesen Spielraum nicht – dort ist die Empfehlung strukturell an das Produktportfolio gebunden. Sie bezahlen bei uns die Einschätzung, nicht das Produkt dahinter.",
      },
    ],
    faq: [
      { q: "Verkaufen Sie auch Software, die Sie empfehlen?", a: "Nein. Wir beraten herstellerunabhängig. Wenn eine Standardlösung besser passt als eine Eigenentwicklung, sagen wir das ehrlich." },
      { q: "Wie läuft eine Beratung ab?", a: "Wir starten mit einem kostenlosen Erstgespräch, analysieren dann Ihre Ist-Situation und liefern einen priorisierten, konkreten Fahrplan." },
      { q: "Wie lange dauert eine Beratung?", a: "Ein Überblicksgespräch ist in ein bis zwei Stunden möglich. Eine fundierte Bestandsaufnahme mit Gesprächen im Team und schriftlichem Fahrplan dauert je nach Unternehmensgröße wenige Tage. Wir sagen Ihnen nach dem kostenlosen Erstgespräch, welcher Umfang für Ihre Situation sinnvoll ist." },
      { q: "Wer sollte aus unserem Unternehmen dabei sein?", a: "Jemand, der entscheiden darf, und jemand, der täglich mit den Systemen arbeitet. Diese zweite Perspektive ist die wichtigere: Die Umwege, die Zeit kosten, kennen die Menschen an der Tastatur – nicht die Geschäftsführung und schon gar nicht wir." },
      { q: "Was passiert nach der Beratung?", a: "Sie bekommen einen priorisierten Fahrplan und können damit tun, was Sie wollen – auch ihn von jemand anderem umsetzen lassen. Es gibt keine Verpflichtung, uns zu beauftragen. Wenn Sie möchten, begleiten wir die Umsetzung; wenn nicht, ist der Fahrplan trotzdem vollständig." },
      { q: "Bewerten Sie auch Software, die wir bereits gekauft haben?", a: "Ja. Oft stellt sich heraus, dass ein System besser ist als sein Ruf und nur falsch eingerichtet oder nie richtig geschult wurde. Eine Neuanschaffung ist dann die teuerste aller Lösungen. Diese Einschätzung bekommen Sie von uns auch dann, wenn sie gegen ein Projekt spricht." },
    ],
    example: {
      title: "Aus fünf Tools wird ein sauberer Workflow",
      persona: "Dienstleister mit 25 Mitarbeitern",
      problem: "Fünf verschiedene Tools, die nicht miteinander sprachen. Daten wurden mehrfach erfasst, niemand hatte den Überblick.",
      solution: "Wir haben die Prozesse analysiert, zwei Tools abgelöst, die restlichen über Schnittstellen verbunden und einen klaren Fahrplan erstellt.",
      result: "Ein durchgängiger Workflow, halb so viel Doppelerfassung und eine IT, die das Wachstum jetzt trägt statt bremst.",
    },
  },
  {
    slug: "unternehmensberatung",
    icon: "chart",
    title: "Unternehmensberatung",
    navTitle: "Unternehmensberatung",
    tagline: "Keine Kunden, keine Aufträge? Dann fangen wir dort an.",
    summary:
      "Für Unternehmen, die straucheln: Wir finden heraus, woran es tatsächlich liegt, und setzen Maßnahmen um, deren Wirkung belegt ist. Keine Motivationsrhetorik, sondern nachprüfbare Schritte.",
    keywords: [
      "Unternehmensberatung Wien KMU",
      "keine Kunden was tun",
      "Umsatz steigern Kleinunternehmen",
      "Beratung Einzelunternehmer Österreich",
      "Unternehmen sanieren Beratung",
    ],
    metaTitle: "Unternehmensberatung Wien für KMU",
    metaDescription:
      "Zu wenig Kunden, zu wenig Aufträge? Wir finden die Ursache und setzen belegbar wirksame Maßnahmen um. Jetzt kostenloses Erstgespräch sichern.",
    heroHeadline: "Sie haben ein gutes Angebot – aber zu wenige Kunden",
    heroSub:
      "Das ist selten ein Qualitätsproblem und fast immer ein Sichtbarkeits-, Positionierungs- oder Prozessproblem. Wir finden heraus, welches davon Ihres ist, und arbeiten mit Maßnahmen, deren Wirkung sich belegen lässt.",
    benefits: [
      { title: "Ursache statt Symptom", text: "Bevor irgendetwas umgesetzt wird, klären wir, woran es tatsächlich hängt. Werbung für ein Positionierungsproblem verbrennt nur Geld." },
      { title: "Belegbare Maßnahmen", text: "Wir arbeiten mit dem, wofür es Belege gibt – nicht mit Erfolgsversprechen, die niemand nachrechnen kann." },
      { title: "Sie sehen die Zahlen", text: "Jede Maßnahme bekommt eine Kennzahl. Was nicht wirkt, wird abgestellt statt schöngeredet." },
    ],
    visual: {
      kind: "matrix",
      caption: "Wenn keine Anfragen kommen – wo liegt es wirklich?",
      good: {
        title: "Zuerst prüfen",
        items: [
          "Sucht überhaupt jemand, was Sie anbieten?",
          "Versteht ein Fremder in zehn Sekunden, was Sie tun?",
          "Kann man Sie erreichen – Nummer anklickbar, Formular kurz?",
          "Wird gemessen, woher Anfragen kommen?",
        ],
      },
      bad: {
        title: "Erst danach sinnvoll",
        items: [
          "Mehr Werbebudget",
          "Eine neue Website",
          "Social-Media-Auftritt auf weiteren Plattformen",
          "Ein neues Logo",
        ],
      },
      note: "Werbung für ein Positionierungsproblem verbrennt nur Geld – schneller als vorher. Deshalb steht die linke Spalte vor jeder Maßnahme.",
    },
    deliverables: [
      "Analyse: Woran es wirklich liegt",
      "Positionierung & Angebotsschärfung",
      "Sichtbarkeit: Website, Suche, Verzeichnisse",
      "Anfragen-Prozess bis zum Abschluss",
      "Kennzahlen & regelmäßige Nachsteuerung",
    ],
    sections: [
      {
        heading: "Warum wir nicht sofort mit Werbung anfangen",
        body:
          "Wenn zu wenige Kunden kommen, ist Werbung der naheliegende Reflex – und oft der teuerste Irrweg. Werbung verstärkt, was bereits da ist. Wenn die Positionierung unklar ist, verstärkt sie die Unklarheit. Wenn die Website Besucher nicht zur Anfrage führt, kauft man teuer Besucher ein, die wieder gehen. Wenn Anfragen liegen bleiben, weil niemand nachfasst, kauft man Anfragen, die verfallen. Deshalb steht am Anfang immer die Frage, an welcher Stelle die Kette reißt. Erst wenn die Kette hält, lohnt es sich, mehr Menschen hineinzuschicken.",
      },
      {
        heading: "Was „wissenschaftlich fundiert“ hier konkret heißt",
        body:
          "Es heißt nicht, dass wir Studien zitieren. Es heißt, dass wir so vorgehen, wie man Wirkung überhaupt feststellen kann: Zustand vorher messen, eine Sache ändern, Wirkung messen, dann die nächste. Wer fünf Maßnahmen gleichzeitig startet und danach mehr Umsatz hat, weiß nicht, welche gewirkt hat – und wiederholt beim nächsten Mal womöglich die falsche. Wir arbeiten deshalb in überschaubaren Schritten mit definierten Kennzahlen und halten fest, was nicht funktioniert hat. Das ist unspektakulärer als ein Zehn-Punkte-Erfolgsplan, aber es ist der Unterschied zwischen Erkenntnis und Zufall.",
      },
      {
        heading: "Die vier Stellen, an denen es meistens hängt",
        body:
          "Erstens die Positionierung: Wer alles für alle macht, wird für nichts empfohlen. Zweitens die Sichtbarkeit: Wer bei Google, in Verzeichnissen und in Empfehlungen nicht vorkommt, existiert für Suchende nicht. Drittens die Umwandlung: Besucher und Anfragen sind da, werden aber nicht zu Aufträgen – schlechte Erreichbarkeit, langsame Angebote, kein Nachfassen. Viertens der Preis und das Angebot selbst: manchmal ist das Angebot schlicht schwer kaufbar, weil zu groß, zu unklar oder ohne Einstiegsmöglichkeit. In den meisten Fällen liegt es an ein bis zwei dieser Punkte, nicht an allen vier.",
      },
      {
        heading: "Was schnell geht – und was nicht",
        body:
          "Ehrlichkeit gehört zur Beratung: Manches wirkt innerhalb von Wochen, anderes braucht Monate. Schnell wirksam sind ein aufgeräumtes Google-Unternehmensprofil, ein funktionierender Anfrageweg, schnellere Angebote, konsequentes Nachfassen und gezielte Anzeigen. Zeit brauchen dagegen Suchmaschinenoptimierung, Bekanntheit und Empfehlungsnetzwerke. Ein seriöser Plan macht beides: die schnellen Hebel zuerst, damit überhaupt Luft entsteht, und parallel den Aufbau dessen, was dauerhaft trägt. Wer Ihnen verspricht, in vier Wochen ein gesundes Unternehmen zu bauen, verkauft Ihnen etwas.",
      },
      {
        heading: "Wann wir absagen",
        body:
          "Wir übernehmen keine Mandate, bei denen wir keinen realistischen Weg sehen. Wenn ein Geschäftsmodell strukturell nicht trägt, wenn die Zahlen eine Insolvenzberatung statt einer Marketingberatung verlangen, oder wenn die Erwartung lautet, dass jemand von außen das Problem ohne Beteiligung des Unternehmers löst – dann sagen wir das im Erstgespräch und nehmen kein Geld. Das kostet uns Aufträge und erspart beiden Seiten eine Enttäuschung.",
      },
    ],
    faq: [
      { q: "Wir sind ein Ein-Personen-Unternehmen. Lohnt sich das?", a: "Gerade dann. Bei kleinen Betrieben hängt oft alles an ein oder zwei Stellen, und die lassen sich schnell finden. Der Umfang der Beratung richtet sich nach Ihrer Größe – wir schnüren kein Konzernpaket für einen Einzelbetrieb." },
      { q: "Was kostet die Beratung?", a: "Das Erstgespräch ist kostenlos und unverbindlich. Danach bekommen Sie einen klaren Rahmen für einen abgegrenzten Umfang, keine offene Stundenabrechnung. Bei Digitalisierungsvorhaben weisen wir zudem auf mögliche Förderungen hin." },
      { q: "Bekommen wir nur einen Plan oder auch Umsetzung?", a: "Beides ist möglich. Viele Beratungen enden mit einem Foliensatz, den niemand umsetzt. Wir können die Maßnahmen auch bauen – Website, Sichtbarkeit, Prozesse – weil wir die Umsetzung ohnehin machen." },
      { q: "Wie schnell sehen wir erste Ergebnisse?", a: "Die schnell wirksamen Hebel zeigen sich meist in vier bis acht Wochen. Was auf Sichtbarkeit bei Google zielt, braucht Monate. Beides sagen wir vorher, damit Sie nicht nach sechs Wochen an der falschen Stelle enttäuscht sind." },
      { q: "Arbeiten Sie auch mit Unternehmen außerhalb der IT?", a: "Ja. Die Ursachen für zu wenige Kunden sind branchenübergreifend erstaunlich ähnlich. Was sich unterscheidet, ist der Weg, auf dem Ihre Kunden suchen – und genau den arbeiten wir für Ihre Branche heraus." },
    ],
    belege: ["elektrobuga", "onyx-cuts"],
    example: {
      title: "Nicht mehr Werbung – ein besserer Anfrageweg",
      persona: "Typisches Szenario: Dienstleister mit 4 Mitarbeitern",
      problem: "Anfragen kamen unregelmäßig, das Werbebudget war bereits erhöht worden ohne erkennbare Wirkung. Angebote gingen nach Tagen raus, Nachfassen fand nicht statt.",
      solution: "Analyse zeigte: Nicht die Sichtbarkeit war das Problem, sondern der Weg von der Anfrage zum Auftrag. Wir haben den Anfrageweg verkürzt, Angebote standardisiert und Nachfassen automatisiert.",
      result: "Dieselbe Zahl an Anfragen führt zu deutlich mehr Aufträgen – ohne zusätzliches Werbebudget. Erst danach war es sinnvoll, die Sichtbarkeit auszubauen.",
    },
  },
  {
    slug: "kuenstliche-intelligenz",
    icon: "sparkles",
    title: "Künstliche Intelligenz (KI)",
    navTitle: "KI-Lösungen",
    tagline: "KI, die im Alltag wirklich Arbeit abnimmt.",
    summary:
      "Von KI-Chatbots über Dokumenten-Automatisierung bis zu maßgeschneiderten KI-Assistenten: Wir bringen künstliche Intelligenz dorthin, wo sie messbar Zeit spart.",
    keywords: [
      "KI Lösungen für Unternehmen",
      "KI Chatbot entwickeln lassen",
      "künstliche Intelligenz KMU",
      "Prozessautomatisierung mit KI",
      "KI Beratung Wien",
    ],
    metaTitle: "KI-Lösungen für Unternehmen & KMU",
    metaDescription:
      "Praktische KI für KMU: Chatbots, automatische Dokumente, Wissens-Assistenten. DSGVO-konform und messbar. Jetzt kostenloses Erstgespräch.",
    heroHeadline: "Künstliche Intelligenz, die im Alltag echte Arbeit abnimmt",
    heroSub:
      "Kein Hype, sondern Werkzeuge, die funktionieren: KI-Chatbots, automatisierte Angebote und Dokumente, intelligente Auswertungen. Wir setzen KI dort ein, wo sie sich sofort auszahlt.",
    benefits: [
      { title: "Sofort spürbar", text: "Wir starten bei den Aufgaben, die heute am meisten Zeit fressen – der Effekt ist schnell messbar." },
      { title: "Ihre Daten bleiben sicher", text: "DSGVO-konform, mit klaren Regeln, wo welche Daten verarbeitet werden." },
      { title: "In Ihre Abläufe integriert", text: "KI als Teil Ihrer bestehenden Tools – kein weiteres Insel-System." },
    ],
    visual: {
      kind: "matrix",
      caption: "Wo KI im Betrieb Stunden spart – und wo Minuten",
      good: {
        title: "Spart Stunden",
        items: [
          "Texte, die immer wieder ähnlich entstehen: Angebote, Antworten",
          "Zahlen aus Belegen und Formularen auslesen",
          "Wissen durchsuchbar machen, das in Ordnern liegt",
          "Lange Dokumente zusammenfassen",
        ],
      },
      bad: {
        title: "Spart Minuten oder gar nichts",
        items: [
          "Ein Chatbot, solange niemand Fragen stellt",
          "Bildgenerierung ohne klaren Zweck",
          "Automatik ohne Messung – sie lernt dann aufs Falsche",
          "Einzelfälle, die jedes Mal eine fachliche Entscheidung brauchen",
        ],
      },
      note: "Angefangen wird fast immer mit dem Spektakulären statt mit dem Teuren. Die Bedarfsanalyse dreht diese Reihenfolge um.",
    },
    deliverables: [
      "KI-Chatbots für Website & Support",
      "Automatische Angebote, E-Mails & Dokumente",
      "Dokumenten- & Rechnungs-Erkennung",
      "Wissens-Assistenten für Ihr Team",
      "Individuelle KI-Integrationen",
    ],
    sections: [
      {
        heading: "KI ohne Hype – der ehrliche Blick",
        body:
          "Nicht jede Aufgabe braucht KI. Wir prüfen zuerst, wo sich der Einsatz wirklich lohnt: bei repetitiven Aufgaben mit vielen Wiederholungen, bei Texten und Dokumenten, bei Kundenanfragen. Dort entlastet KI Ihr Team spürbar. Sie bekommen von uns keine Technik-Show, sondern eine klare Rechnung: Was kostet es, was spart es?",
      },
      {
        heading: "Beispiele, die sofort wirken",
        body:
          "Ein KI-Chatbot, der 70 % der Standardanfragen auf Ihrer Website beantwortet. Eine Automatisierung, die aus einer E-Mail-Anfrage in Sekunden ein fertiges Angebot macht. Eine Dokumenten-Erkennung, die Rechnungen automatisch ausliest und verbucht. Genau diese Bausteine bauen wir passgenau für Ihr Unternehmen.",
      },
      {
        heading: "Sicherheit & Datenschutz",
        body:
          "Gerade bei KI ist Vertrauen entscheidend. Wir arbeiten DSGVO-konform, transparent und mit klaren Grenzen: Sie wissen jederzeit, welche Daten wie verarbeitet werden. Wo möglich, setzen wir auf Lösungen, bei denen sensible Daten in Ihrer Kontrolle bleiben.",
      },
      {
        heading: "Welche Aufgaben sich für KI eignen – und welche nicht",
        body:
          "Geeignet ist alles, was oft vorkommt, einem Muster folgt und bei gelegentlichen Fehlern nicht teuer wird: wiederkehrende Kundenanfragen, das Auslesen von Dokumenten, das Erstellen von Textentwürfen, das Zusammenfassen langer Unterlagen, das Vorsortieren von Eingängen. Ungeeignet ist alles, wo ein Fehler unmittelbar Schaden anrichtet und niemand ihn bemerkt: verbindliche Preiszusagen, rechtliche oder medizinische Auskünfte, endgültige Entscheidungen über Menschen. Die Grenze verläuft nicht entlang der Technik, sondern entlang der Frage, was passiert, wenn das Ergebnis falsch ist. Wo ein Mensch das Ergebnis ohnehin prüft, ist KI ein Beschleuniger. Wo niemand prüft, ist sie ein Risiko. Wir bauen deshalb konsequent mit Prüfschleife: Die KI schlägt vor, Ihr Team bestätigt.",
      },
      {
        heading: "Was KI kostet und wie sich der Nutzen rechnen lässt",
        body:
          "Kosten entstehen an zwei Stellen: einmalig für den Aufbau, laufend für die Nutzung der Sprachmodelle. Der laufende Teil wird oft überschätzt – für die typische Menge an Kundenanfragen eines KMU bewegt er sich im Bereich weniger Euro bis niedriger zweistelliger Beträge pro Monat. Wir bauen zusätzlich eine Obergrenze ein, damit kein Ausreißer entstehen kann. Der Nutzen lässt sich vorab abschätzen: Zählen Sie eine Woche lang, wie viele gleichartige Anfragen eintreffen und wie lange die Beantwortung dauert. Das ergibt eine belastbare Zahl, an der sich das Vorhaben messen lassen muss. Fällt die Rechnung negativ aus, raten wir ab – auch das kommt vor.",
      },
      {
        heading: "Wo Ihre Daten verarbeitet werden",
        body:
          "Das ist bei KI die entscheidende Frage, und sie wird zu selten gestellt. Wir klären vor der Umsetzung, welche Daten überhaupt an ein Modell gehen und welche vorher entfernt werden. Wo möglich, verarbeiten wir innerhalb der EU. Bei besonders sensiblen Inhalten – Gesundheitsdaten, Personalakten, Mandantenunterlagen – setzen wir auf Modelle, die in Ihrer eigenen Infrastruktur laufen; das ist aufwendiger, aber die einzige Variante, bei der die Daten Ihr Haus nicht verlassen. Sie bekommen von uns eine schriftliche Aufstellung, welche Daten wohin fließen, damit Ihr Datenschutzbeauftragter das bewerten kann. Ohne diese Aufstellung sollten Sie kein KI-Projekt starten, bei uns nicht und anderswo erst recht nicht.",
      },
      {
        heading: "Warum viele KI-Projekte im Sand verlaufen",
        body:
          "Der häufigste Grund ist, dass mit der Technik begonnen wird statt mit dem Problem. Es entsteht ein beeindruckender Prototyp, der niemandem im Alltag hilft, und nach der Begeisterung folgt Stille. Der zweite Grund ist fehlende Einbettung: Eine KI-Funktion, die in einem eigenen Fenster lebt und zu der man extra hinwechseln muss, wird nicht benutzt. Sie muss dort sitzen, wo die Arbeit ohnehin stattfindet. Der dritte Grund ist Misstrauen – berechtigt, wenn nie jemand erklärt hat, wie die Ergebnisse zustande kommen und wo die Grenzen liegen. Wir beginnen deshalb bei einem konkreten Ärgernis, integrieren in bestehende Abläufe und legen offen, was das System kann und was nicht.",
      },
    ],
    faq: [
      { q: "Sind meine Daten bei KI-Lösungen sicher?", a: "Ja. Wir arbeiten DSGVO-konform und legen offen, welche Daten wo verarbeitet werden. Sensible Daten schützen wir mit passenden Maßnahmen." },
      { q: "Lohnt sich KI auch für kleine Unternehmen?", a: "Oft gerade dann. Kleine Teams profitieren am meisten, wenn KI wiederkehrende Aufgaben übernimmt und Freiräume schafft." },
      { q: "Ersetzt KI Mitarbeiter?", a: "In den Projekten, die wir umsetzen, nicht. Wir automatisieren Aufgaben, die niemand gerne macht und die keinen Kundenkontakt brauchen – Abtippen, Vorsortieren, Standardauskünfte. Was dadurch frei wird, ist Zeit für die Fälle, in denen ein Mensch tatsächlich gebraucht wird. Wo ein Vorhaben auf Personalabbau zielt, ist das eine unternehmerische Entscheidung, die Sie treffen, nicht die Technik." },
      { q: "Woher weiß die KI etwas über unser Unternehmen?", a: "Wir stellen ihr genau die Quellen zur Verfügung, die sie kennen soll – Ihre Leistungsbeschreibungen, häufige Fragen, interne Handbücher. Sie antwortet dann aus diesem Material und nicht aus allgemeinem Wissen. Das ist der entscheidende Unterschied zu einem frei erfindenden System." },
      { q: "Was passiert, wenn die KI etwas Falsches sagt?", a: "Wir begrenzen sie auf Ihre eigenen Inhalte und legen Themen fest, die sie grundsätzlich nicht beantwortet, sondern weitergibt. Sie sehen alle Verläufe und können nachschärfen. Eine hundertprozentige Garantie gibt es bei keiner KI – deshalb gehören verbindliche Zusagen niemals in ein automatisches System." },
      { q: "Brauchen wir dafür eigene Server?", a: "Nur wenn die Daten es verlangen. Für unkritische Anwendungsfälle genügen Dienste mit Verarbeitung in der EU. Bei Gesundheits-, Personal- oder Mandantendaten empfehlen wir Modelle in Ihrer eigenen Infrastruktur. Was in Ihrem Fall angemessen ist, klären wir vor der Umsetzung." },
    ],
    example: {
      title: "Der Chatbot, der nie schläft",
      persona: "Online-Shop im Kundenservice",
      problem: "Immer dieselben Fragen zu Versand, Retouren und Verfügbarkeit – das Support-Team kam kaum hinterher.",
      solution: "Wir haben einen KI-Chatbot gebaut, der die Produkt- und Versandinfos kennt und rund um die Uhr antwortet – mit sauberer Übergabe an einen Menschen, wenn es komplex wird.",
      result: "Rund 70 % der Anfragen werden sofort beantwortet, das Team hat wieder Zeit für die kniffligen Fälle, die Kunden warten nicht mehr.",
    },
  },
  {
    slug: "webdesign",
    kern: true,
    weiterfuehrend: [
      { label: "Websites, die wir gebaut haben – mit Bildschirmfotos", href: "/websites" },
      { label: "Ratgeber: Was kostet eine Website?", href: "/ratgeber/was-kostet-eine-website" },
      { label: "Ratgeber: Baukasten oder eigene Website?", href: "/ratgeber/baukasten-oder-eigene-website" },
    ],
    icon: "dashboard",
    title: "Webdesign & Websites",
    navTitle: "Webdesign",
    tagline: "Eine Website, die Anfragen bringt – nicht nur gut aussieht.",
    summary:
      "Websites, die schnell laden, auf jedem Gerät funktionieren und Besucher zu Anfragen machen. Gebaut auf einer Technik, die auch bei Google besteht – nicht in einem Baukasten zusammengeklickt.",
    keywords: [
      "Webdesign Wien",
      "Website erstellen lassen Österreich",
      "Homepage für KMU",
      "Website Relaunch Wien",
      "responsive Webdesign",
    ],
    metaTitle: "Webdesign Wien | Websites die Anfragen bringen",
    metaDescription:
      "Websites aus Wien, die schnell laden und Besucher zu Anfragen machen. Technisch sauber, mobil optimiert. Jetzt kostenloses Erstgespräch sichern.",
    heroHeadline: "Ihre Website soll arbeiten, nicht nur existieren",
    heroSub:
      "Viele Websites sind hübsch und bringen trotzdem nichts. Wir bauen Seiten, die schnell laden, auf dem Handy funktionieren und den Besucher zur Anfrage führen – messbar, nicht nach Gefühl.",
    benefits: [
      { title: "Auf Anfragen gebaut", text: "Jede Seite hat ein Ziel. Aufbau, Texte und Handlungsaufforderungen folgen diesem Ziel statt dem Zufall." },
      { title: "Schnell auf jedem Gerät", text: "Ladezeit und mobile Bedienung sind keine Nachbesserung, sondern von Anfang an eingeplant – Google bewertet beides." },
      { title: "Sie können selbst pflegen", text: "Texte und Bilder ändern Sie ohne uns. Für alles Weitere sind wir da, aber Sie sind nicht darauf angewiesen." },
    ],
    visual: {
      kind: "compare",
      caption: "Baukasten oder gebaute Website",
      columns: ["Baukasten", "Gebaute Website"],
      rows: [
        { criterion: "Schnell und günstig online", a: "yes", b: "no" },
        { criterion: "Ladezeit und Technik beeinflussbar", a: "no", b: "yes" },
        { criterion: "Auf Anfragen hin aufgebaut", a: "part", b: "yes" },
        { criterion: "Unabhängig von Preisänderungen des Anbieters", a: "no", b: "yes" },
        { criterion: "Umzug zu einem anderen Anbieter möglich", a: "no", b: "yes" },
        { criterion: "Ohne Hilfe selbst zu starten", a: "yes", b: "no" },
      ],
      note: "Für eine Visitenkarte im Netz reicht ein Baukasten. Sobald die Seite Anfragen bringen soll, entscheidet, was man an Technik und Aufbau noch ändern kann.",
    },
    deliverables: [
      "Konzept, Aufbau & Seitenstruktur",
      "Gestaltung in Ihrem Erscheinungsbild",
      "Texte, die zur Anfrage führen",
      "Technische Suchmaschinenoptimierung von Beginn an",
      "Pflegemöglichkeit für Ihr Team",
    ],
    sections: [
      {
        heading: "Warum schöne Websites oft nichts bringen",
        body:
          "Die meisten Websites scheitern nicht am Aussehen, sondern an der Frage, was der Besucher als Nächstes tun soll. Er landet auf einer Startseite, liest ein paar Sätze über das Unternehmen und findet keinen offensichtlichen nächsten Schritt. Also geht er zurück zu Google. Eine Website, die Anfragen bringt, ist entlang der Fragen des Besuchers gebaut: Bin ich hier richtig, kann das jemand für mich lösen, was kostet es ungefähr, wie geht es weiter. Wenn diese vier Fragen in dieser Reihenfolge beantwortet werden, entsteht eine Anfrage. Wenn stattdessen die Firmengeschichte am Anfang steht, entsteht keine.",
      },
      {
        heading: "Baukasten oder eigene Umsetzung",
        body:
          "Für eine reine Visitenkarte im Netz genügt ein Baukasten, und wir raten dann auch dazu – es wäre unehrlich, dafür ein Angebot zu schreiben. Sinnvoll wird eine eigene Umsetzung, sobald die Website Arbeit übernehmen soll: Terminbuchung, Kundenbereich, Anbindung an Ihre Systeme, viele Unterseiten für Suchmaschinen, oder schlicht Ladezeiten, die im Baukasten nicht erreichbar sind. Der Unterschied zeigt sich meist nicht am ersten Tag, sondern nach einem Jahr – wenn Sie etwas ändern wollen, das der Baukasten nicht vorsieht.",
      },
      {
        heading: "Ladezeit ist kein Technikthema, sondern ein Geschäftsthema",
        body:
          "Jede zusätzliche Sekunde Ladezeit kostet Besucher, und zwar überproportional auf dem Handy und im Mobilfunknetz. Google bezieht die Ladezeit zudem direkt in die Bewertung ein. Wir bauen deshalb ohne überflüssige Zusatzmodule, optimieren Bilder automatisch und liefern nur das aus, was gebraucht wird. Das ist unspektakulär und wird selten beauftragt – es entscheidet aber darüber, ob jemand Ihre Seite überhaupt sieht.",
      },
      {
        heading: "Was eine Website kostet",
        body:
          "Der Rahmen hängt an der Zahl der Seiten, daran ob Texte und Bilder vorhanden sind, und daran ob Funktionen wie Terminbuchung oder ein Kundenbereich dazukommen. Eine überschaubare Unternehmenswebsite mit klarer Struktur liegt deutlich unter dem, was ein individuelles Softwareprojekt kostet. Wir nennen nach dem Erstgespräch einen Fixpreis-Rahmen für einen definierten Umfang – und sagen ehrlich, wenn ein Baukasten für Ihren Fall die vernünftigere Wahl ist.",
      },
    ],
    faq: [
      { q: "Können wir Texte und Bilder selbst ändern?", a: "Ja. Sie bekommen einen Zugang, über den sich Inhalte ohne technische Kenntnisse pflegen lassen. Für strukturelle Änderungen sind wir da – aber der Alltag läuft ohne uns." },
      { q: "Übernehmen Sie auch die Texte?", a: "Auf Wunsch ja. Gute Websitetexte sind kein Beiwerk, sondern der Teil, der über eine Anfrage entscheidet. Wir schreiben sie gemeinsam mit Ihnen, weil das Fachwissen bei Ihnen liegt." },
      { q: "Was passiert mit unserer bestehenden Website?", a: "Wenn sie inhaltlich brauchbar ist, übernehmen wir Inhalte und richten Weiterleitungen ein, damit die bisherige Sichtbarkeit bei Google nicht verloren geht. Das wird bei Neubauten oft vergessen und kostet dann Monate an Aufbauarbeit." },
      { q: "Ist Suchmaschinenoptimierung enthalten?", a: "Die technische Grundlage ja – saubere Struktur, Ladezeit, mobile Darstellung, strukturierte Daten. Laufende inhaltliche Optimierung ist eine eigene Leistung, weil sie dauerhaft stattfindet und nicht mit dem Livegang endet." },
    ],
    belege: ["elektrobuga", "pizza-schwarzlackenau", "onyx-cuts"],
    example: {
      title: "Von der Visitenkarte zur Anfragequelle",
      persona: "Typisches Szenario: Dienstleister mit veralteter Website",
      problem: "Die Website war zehn Jahre alt, am Handy kaum bedienbar und bestand aus drei Seiten. Anfragen kamen ausschließlich über Empfehlungen.",
      solution: "Neuaufbau mit klarer Struktur, eigener Seite je Leistung, schnellen Ladezeiten und einem Anfrageformular, das nach dem Bedarf fragt statt nach einer Nachricht.",
      result: "Die Website führt Besucher zur Anfrage statt sie zu informieren – und ist die Grundlage, auf der Suchmaschinenoptimierung überhaupt wirken kann.",
    },
  },
  {
    slug: "suchmaschinenoptimierung-seo",
    kern: true,
    icon: "search",
    title: "Suchmaschinenoptimierung (SEO)",
    navTitle: "SEO",
    tagline: "Gefunden werden, wenn Kunden nach Ihnen suchen.",
    summary:
      "Nachhaltige SEO für KMU: technisch sauber, inhaltlich stark, lokal sichtbar. Wir sorgen dafür, dass Sie bei Google dort auftauchen, wo Ihre Kunden suchen.",
    keywords: [
      "SEO Agentur Wien",
      "Suchmaschinenoptimierung Österreich",
      "lokale SEO Wien",
      "bei Google gefunden werden",
      "SEO für KMU",
    ],
    metaTitle: "SEO Agentur Wien | Bei Google gefunden",
    metaDescription:
      "Nachhaltige SEO aus Wien: Technik, Inhalte, lokale Sichtbarkeit. Ohne Knebelvertrag, messbar mehr Anfragen. Jetzt kostenloses Erstgespräch.",
    heroHeadline: "Bei Google gefunden werden – von den Kunden, die Sie wollen",
    heroSub:
      "Wir bringen Ihre Website technisch und inhaltlich nach vorne: für die Suchbegriffe, mit denen Ihre Wunschkunden nach Ihnen suchen. Nachhaltig, messbar, ohne Knebelverträge.",
    benefits: [
      { title: "Die richtigen Suchbegriffe", text: "Wir optimieren auf Anfragen mit echter Kaufabsicht – nicht auf leere Klick-Zahlen." },
      { title: "Technisch & inhaltlich", text: "Schnelle Ladezeiten, saubere Struktur und Texte, die Menschen und Google überzeugen." },
      { title: "Transparent messbar", text: "Sie sehen schwarz auf weiß, wie Rankings, Besucher und Anfragen steigen." },
    ],
    visual: {
      kind: "compare",
      caption: "Kopf-Keyword oder konkrete Suchanfrage",
      columns: ["„Tischler Wien“", "„Einbauschrank Dachschräge“"],
      rows: [
        { criterion: "Viele Suchende", a: "yes", b: "no" },
        { criterion: "Dahinter steckt ein konkretes Vorhaben", a: "part", b: "yes" },
        { criterion: "In Monaten erreichbar", a: "no", b: "yes" },
        { criterion: "Wenig Wettbewerb", a: "no", b: "yes" },
        { criterion: "Führt direkt zu einer Anfrage", a: "part", b: "yes" },
        { criterion: "Gut für die Bekanntheit", a: "yes", b: "no" },
      ],
      note: "Zehn Seiten der rechten Art bringen in Summe mehr Anfragen als ein halber Erfolg bei der linken – und sie sind in Monaten erreichbar statt in Jahren.",
    },
    deliverables: [
      "SEO-Analyse & Keyword-Strategie",
      "Technische Optimierung (Speed, Struktur, Core Web Vitals)",
      "Suchmaschinenoptimierte Inhalte & Landingpages",
      "Lokale SEO & Google-Unternehmensprofil",
      "Laufendes Monitoring & Reporting",
    ],
    sections: [
      {
        heading: "Warum SEO für KMU der beste Marketing-Hebel ist",
        body:
          "Wer bei Google sucht, hat bereits einen Bedarf. Wenn Sie genau dann sichtbar sind, gewinnen Sie Kunden mit hoher Kaufabsicht – ohne für jeden Klick zu bezahlen. Anders als bezahlte Anzeigen wirkt gute SEO langfristig: Ein Artikel, der heute rankt, bringt auch in einem Jahr noch Anfragen. Genau deshalb ist Suchmaschinenoptimierung für viele KMU der nachhaltigste Marketing-Kanal.",
      },
      {
        heading: "Long-Tail-Keywords: gezielt statt breit",
        body:
          "Statt gegen große Marken um ein einzelnes, hart umkämpftes Wort zu kämpfen, setzen wir auf konkrete Suchanfragen mit klarer Absicht – zum Beispiel „individuelle Software für Handwerksbetrieb Wien\" statt nur „Software\". Diese Long-Tail-Keywords sind einfacher zu gewinnen und bringen genau die Besucher, die kaufen wollen. Diese Website ist selbst nach diesem Prinzip aufgebaut.",
      },
      {
        heading: "Technische SEO & Ladezeit",
        body:
          "Google bewertet, wie schnell und stabil Ihre Seite lädt (Core Web Vitals). Wir sorgen für saubere Technik: schnelle Ladezeiten, mobile Optimierung, korrekte Struktur (strukturierte Daten) und eine Architektur, die Suchmaschinen mühelos verstehen. Das ist die Basis, ohne die auch die besten Inhalte nicht ranken.",
      },
      {
        heading: "Wie lange SEO wirklich braucht",
        body:
          "Wer Ihnen Ergebnisse in vier Wochen verspricht, hat entweder eine sehr einfache Nische im Blick oder erzählt Ihnen nicht die Wahrheit. Realistisch ist folgender Verlauf: In den ersten Wochen passiert technisch viel, sichtbar wenig – Google muss die Änderungen erst erfassen. Nach etwa zwei bis drei Monaten zeigen sich erste Bewegungen bei konkreten, wenig umkämpften Suchanfragen. Nach sechs bis zwölf Monaten wird daraus ein spürbarer Anfragestrom, sofern parallel Inhalte entstehen und die Website an Bekanntheit gewinnt. Bei stark umkämpften Begriffen wie einer Branchenbezeichnung plus Großstadt dauert es länger, oft Jahre, und hängt weniger von der Technik ab als davon, wer sonst noch auf Sie verweist. Wir sagen Ihnen vorab, in welche Kategorie Ihre Wunschbegriffe fallen.",
      },
      {
        heading: "Warum Position 11 wertvoller ist als Position 40",
        body:
          "Die Klickrate fällt nicht gleichmäßig ab, sondern bricht ein. Auf Position 1 klicken je nach Branche zwischen einem Viertel und einem Drittel aller Suchenden, auf Position 5 noch etwa jeder Zwanzigste, auf Position 10 rund zwei von hundert. Ab Seite 2 geht es gegen null, weil kaum jemand weiterblättert. Das hat eine praktische Folge für die Reihenfolge der Arbeit: Eine Seite von Position 40 auf 20 zu bringen, ändert am Anfragevolumen fast nichts. Dieselbe Anstrengung bei einer Seite, die auf Position 12 steht, kann das Vielfache bringen. Wir suchen deshalb zuerst die Suchanfragen, bei denen Sie knapp hinter der ersten Seite stehen, und arbeiten dort – nicht dort, wo der Weg am weitesten ist.",
      },
      {
        heading: "Was wir messen und wie wir berichten",
        body:
          "Rankings allein sind eine Eitelkeitskennzahl. Was zählt, sind Anfragen. Wir richten die Messung deshalb so ein, dass nachvollziehbar wird, welche Suchanfrage zu welchem Besuch und welcher Besuch zu welcher Anfrage geführt hat. Grundlage sind die Google Search Console für Sichtbarkeit und Klickraten sowie eine datenschutzkonforme Auswertung des Verhaltens auf der Website. Im Bericht steht, was sich verändert hat, was wir dafür getan haben und was als Nächstes ansteht – in verständlicher Sprache, nicht als Zahlenfriedhof. Wenn etwas nicht funktioniert hat, steht das ebenfalls drin.",
      },
      {
        heading: "Woran Sie unseriöse SEO-Angebote erkennen",
        body:
          "Es gibt einige verlässliche Warnzeichen. Garantierte Platzierungen sind das deutlichste – niemand kann sie zusagen, weil niemand außer Google die Reihenfolge bestimmt. Ebenso der Verkauf von Backlink-Paketen: Gekaufte Verweise verstoßen gegen Googles Richtlinien und können zu einer manuellen Abstrafung führen, die schwerer zu beheben ist als das ursprüngliche Problem. Auffällig sind auch lange Vertragsbindungen ohne inhaltliche Zwischenziele und Berichte, die ausschließlich Rankings zeigen, aber keine Anfragen. Wir arbeiten ausschließlich im Rahmen dessen, was Google in seinen Richtlinien beschreibt: verständliche Inhalte, saubere Technik, echte Erwähnungen. Das dauert länger und hält dafür.",
      },
    ],
    faq: [
      { q: "Wie schnell wirkt SEO?", a: "Erste Effekte sieht man oft nach wenigen Wochen, die volle Wirkung nach einigen Monaten. Dafür ist sie nachhaltig – anders als Anzeigen, die sofort stoppen, wenn das Budget endet." },
      { q: "Gibt es lange Vertragsbindungen?", a: "Nein. Wir arbeiten transparent und ohne Knebelverträge. Sie bleiben, weil die Ergebnisse stimmen." },
      { q: "Können Sie Platz 1 garantieren?", a: "Nein, und niemand kann das seriös. Die Reihenfolge bestimmt allein Google, und sie ändert sich laufend. Wer Platzierungen garantiert, arbeitet entweder mit sehr einfachen Nischenbegriffen oder mit Methoden, die gegen Googles Richtlinien verstoßen und Ihrer Domain langfristig schaden." },
      { q: "Wie stark hängt SEO davon ab, wer sonst auf uns verweist?", a: "Stark, besonders bei umkämpften Begriffen. Inhalt und Technik können wir vollständig beeinflussen; Verweise von anderen Websites entstehen dagegen dadurch, dass es Sie gibt und dass über Sie gesprochen wird. Branchenverzeichnisse, Fachbeiträge, Partnerschaften und Presse zahlen darauf ein. Gekaufte Verweise nicht – die sind ein Risiko, kein Hebel." },
      { q: "Warum ist eine junge Domain im Nachteil?", a: "Google bewertet nicht nur die einzelne Seite, sondern das Vertrauen in die gesamte Website. Das entsteht über Zeit, Inhalte und Erwähnungen. Eine Domain aus dem letzten Jahr startet deshalb hinter einer, die seit zehn Jahren zum Thema publiziert – auch bei gleichwertigem Inhalt. Aufholen ist möglich, dauert aber und geht am schnellsten über konkrete Nischenbegriffe statt über die großen Schlagworte." },
      { q: "Gibt es eine Mindestlaufzeit?", a: "Nein. Wir arbeiten ohne Knebelverträge. Sinnvoll ist ein Zeitraum von mindestens sechs Monaten, weil vorher schlicht keine belastbare Wirkung messbar ist – aber das ist eine Empfehlung, keine Bindung." },
    ],
    belege: ["umzug-alpenland", "formularservice", "elektrobuga"],
    example: {
      title: "Von unsichtbar zur ersten Seite",
      persona: "Lokaler Dienstleister in Wien",
      problem: "Die Website war schön, aber bei Google praktisch unsichtbar. Neue Kunden kamen fast nur über Empfehlung.",
      solution: "Wir haben Keyword-Strategie, Technik und Inhalte überarbeitet und gezielte Landingpages für die wichtigsten Leistungen erstellt.",
      result: "Deutlich mehr Sichtbarkeit für die relevanten Suchbegriffe – und regelmäßig Anfragen über die Website statt nur über Mundpropaganda.",
    },
  },
  {
    slug: "google-ads",
    kern: true,
    icon: "target",
    title: "Google Ads",
    navTitle: "Google Ads",
    tagline: "Sofort sichtbar – mit Budget, das sich rechnet.",
    summary:
      "Profitable Google-Ads-Kampagnen für KMU: klar aufgesetzt, laufend optimiert, ehrlich abgerechnet. Sie zahlen für Anfragen, nicht für Klick-Statistiken.",
    keywords: [
      "Google Ads Agentur Wien",
      "Performance Marketing Wien",
      "Google Ads für KMU",
      "SEA Österreich",
      "Google Werbung schalten lassen",
      "datengetriebenes Online-Marketing",
    ],
    metaTitle: "Google Ads Agentur Wien für KMU",
    metaDescription:
      "Google Ads aus Wien: auf Anfragen optimiert statt auf Klicks. Faire Betreuung ohne Knebelvertrag. Jetzt kostenloses Erstgespräch sichern.",
    heroHeadline: "Sofort ganz oben bei Google – mit Budget, das sich auszahlt",
    heroSub:
      "Wir schalten und optimieren Google-Ads-Kampagnen, die auf ein Ziel ausgerichtet sind: Anfragen und Umsatz. Kein verbranntes Budget, keine leeren Klick-Statistiken.",
    benefits: [
      { title: "Schnell sichtbar", text: "Anders als SEO wirken Anzeigen sofort – ideal für Angebote, die jetzt Kunden brauchen." },
      { title: "Auf Umsatz optimiert", text: "Wir messen, was zählt: Anfragen und Abschlüsse, nicht nur Klicks und Impressionen." },
      { title: "Ehrlich abgerechnet", text: "Transparente Betreuung, klare Zahlen, keine versteckten Aufschläge auf Ihr Werbebudget." },
    ],
    visual: {
      kind: "matrix",
      caption: "Wann Google Ads für ein Angebot taugt",
      good: {
        title: "Spricht dafür",
        items: [
          "Menschen suchen aktiv nach dem, was Sie anbieten",
          "Ein Auftrag trägt mehr als ein paar Euro Deckungsbeitrag",
          "Es gibt eine Seite, die Anfragen annehmen kann",
          "Mindestens rund 300 € Budget im Monat sind möglich",
        ],
      },
      bad: {
        title: "Spricht dagegen",
        items: [
          "Das Angebot ist so neu, dass niemand danach sucht",
          "Die Marge trägt keine Werbekosten",
          "Die Website hat kein Formular und keine anklickbare Nummer",
          "Anfragen können gerade gar nicht bearbeitet werden",
        ],
      },
      note: "Der letzte Punkt links ist keine Preisfrage, sondern Statistik: Unter dieser Grenze entstehen zu wenige Daten, damit das Konto überhaupt lernen kann.",
    },
    deliverables: [
      "Konten-Setup & Keyword-Recherche",
      "Anzeigen & Kampagnen-Struktur",
      "Landingpage-Optimierung für mehr Conversions",
      "Laufende Optimierung & A/B-Tests",
      "Transparentes Reporting",
    ],
    sections: [
      {
        heading: "Wann Google Ads die richtige Wahl ist",
        body:
          "Google Ads ist ideal, wenn Sie schnell sichtbar sein wollen – bei einer Neueröffnung, für ein zeitkritisches Angebot oder in einem umkämpften Markt. Während SEO seine Wirkung über Monate aufbaut, liefern Anzeigen ab dem ersten Tag Besucher. Am stärksten ist die Kombination: Anzeigen für den schnellen Effekt, SEO für die nachhaltige Basis.",
      },
      {
        heading: "Warum viele Kampagnen Geld verbrennen",
        body:
          "Schlecht aufgesetzte Kampagnen zahlen für falsche Suchbegriffe, schicken Besucher auf die Startseite statt auf eine passende Landingpage und messen die falschen Dinge. Wir drehen das um: präzises Targeting, passende Landingpages und ein sauberes Conversion-Tracking. So fließt Ihr Budget in echte Anfragen statt in Streuverluste.",
      },
      {
        heading: "KI-gestützt & fair betreut",
        body:
          "Mit KI-Unterstützung testen und optimieren wir schneller – mehr Anzeigenvarianten, schnellere Auswertung, bessere Ergebnisse pro Euro. Und wir betreuen fair: keine langen Bindungen, keine versteckten Aufschläge. Sie sehen genau, wohin Ihr Budget fließt und was es bringt.",
      },
      {
        heading: "Wie sich ein sinnvolles Budget bestimmen lässt",
        body:
          "Statt mit einer Wunschzahl zu starten, rechnen wir rückwärts. Was ist Ihnen ein neuer Kunde wert, wie viele Anfragen brauchen Sie im Schnitt für einen Abschluss, und was darf eine Anfrage folglich kosten? Daraus ergibt sich, welcher Klickpreis tragbar ist und wie viel Budget nötig ist, um überhaupt genug Daten für eine Optimierung zu sammeln. Zu kleine Budgets sind ein häufiger Fehler: Unterhalb einer gewissen Schwelle sammelt das Konto nie genug Signale, um besser zu werden, und das Geld ist ohne Erkenntnisgewinn weg. Wir sagen Ihnen ehrlich, ob Ihr Vorhaben mit dem geplanten Budget sinnvoll ist – und raten ab, wenn nicht.",
      },
      {
        heading: "Warum der Qualitätsfaktor Ihren Klickpreis senkt",
        body:
          "Google versteigert Anzeigenplätze nicht allein nach Gebot. Wer relevantere Anzeigen und passendere Zielseiten liefert, zahlt für dieselbe Position weniger. Dieser Zusammenhang wird oft unterschätzt, ist aber der Grund, warum ein gut aufgesetztes Konto mit demselben Budget deutlich mehr erreicht. Praktisch heißt das: Suchbegriff, Anzeigentext und Zielseite müssen zusammenpassen. Wer alle Anzeigen auf die Startseite leitet, zahlt Aufschlag. Deshalb bauen wir passende Zielseiten – und deshalb greifen bei uns Suchmaschinenoptimierung und Anzeigen ineinander statt nebeneinander zu laufen.",
      },
      {
        heading: "Wem das Konto und die Daten gehören",
        body:
          "Das Werbekonto läuft auf Ihren Namen, nicht auf unseren. Sie haben jederzeit vollen Zugriff, sehen jede Ausgabe und können uns die Rechte entziehen, ohne Ihre Historie zu verlieren. Diese Historie ist wertvoll: Ein Konto, das seit Monaten Daten sammelt, arbeitet besser als ein frisches. Manche Agenturen betreiben Kundenwerbung über eigene Sammelkonten – dann beginnen Sie bei einem Wechsel wieder bei null. Wir rechnen außerdem transparent ab: Ihr Werbebudget geht direkt an Google, unsere Betreuung wird getrennt ausgewiesen. Kein prozentualer Aufschlag, der mit steigendem Budget stillschweigend mitwächst.",
      },
      {
        heading: "Wie wir mit KI arbeiten – und wo Google sie selbst einsetzt",
        body:
          "Zwei Ebenen, die oft verwechselt werden. Die erste ist unsere Arbeit: Wir erzeugen Anzeigentexte in vielen Varianten, werten Suchbegriffe, Zielseiten und Anfragen in großer Menge aus und finden darin Muster, für die man von Hand Tage bräuchte. Die zweite steckt in Google Ads selbst: Die automatischen Gebotsstrategien entscheiden bei jeder einzelnen Suchanfrage neu, ob und wie viel geboten wird. Das klingt nach Zauberei und ist doch simpel – die Automatik ist nur so gut wie die Rückmeldung, die sie bekommt. Wer ihr Klicks meldet, bekommt Klicks. Wer ihr Anfragen meldet, bekommt Anfragen. Genau deshalb steht bei uns die Messung vor der Kampagne, und deshalb sind Tag Manager und Analytics kein Zusatz, sondern der Kern der Arbeit.",
      },
      {
        heading: "Was „passiv Anfragen bekommen“ realistisch heißt",
        body:
          "Das Ziel ist ein Aufbau, der ohne tägliches Zutun Anfragen liefert: Die Anzeigen laufen, die Messung zählt mit, das Konto lernt aus jeder Anfrage, und die Zielseiten sind so gebaut, dass aus Besuchern Anrufe werden. In diesem Zustand schauen Sie in einen Monatsbericht statt in die Kontooberfläche. Was dabei nicht stimmt, ist das Bild vom Selbstläufer: Jede Woche kommen neue Suchbegriffe hinzu, von denen ein Teil nichts bringt und ausgeschlossen gehört; Mitbewerber ändern ihre Gebote; Saison und Nachfrage schwanken. Diese Pflege ist unsere Aufgabe und der Grund, warum es eine laufende Betreuung gibt – nicht ein Aufsetzen und Liegenlassen.",
      },
      {
        heading: "Anzeigen oder Suchmaschinenoptimierung – was zuerst?",
        body:
          "Beides hat einen anderen Zweck. Anzeigen liefern ab dem ersten Tag Besucher und hören auf, sobald das Budget endet. Suchmaschinenoptimierung braucht Monate und wirkt danach weiter. Für ein Unternehmen, das jetzt Aufträge braucht, sind Anzeigen der richtige Einstieg. Für eines, das langfristig unabhängig von Werbeausgaben werden will, ist es Optimierung. In der Praxis ergänzen sie sich am besten: Die Anzeigen zeigen innerhalb weniger Wochen, welche Suchbegriffe tatsächlich zu Anfragen führen – und genau diese Erkenntnis fließt in die Inhalte ein, die langfristig ohne Werbebudget ranken sollen. Anzeigen sind damit nicht nur Umsatz, sondern auch Marktforschung.",
      },
    ],
    faq: [
      { q: "Wie viel Budget brauche ich für Google Ads?", a: "Das hängt von Branche und Region ab. Wir starten bewusst schlank, messen genau und skalieren erst, wenn die Zahlen stimmen." },
      { q: "Was kostet die Betreuung?", a: "Sie bekommen ein faires, transparentes Betreuungsmodell ohne versteckte Aufschläge auf Ihr Werbebudget. Details im kostenlosen Erstgespräch." },
      { q: "Ab welchem Budget ist Google Ads sinnvoll?", a: "Das hängt von Branche und Region ab; entscheidend ist, ob genug Klicks zusammenkommen, um überhaupt etwas lernen zu können. Wir rechnen das vor dem Start gemeinsam durch und sagen ehrlich, wenn Ihr Vorhaben mit dem geplanten Budget keinen Sinn ergibt." },
      { q: "Wie schnell kommen die ersten Anfragen?", a: "Anzeigen laufen innerhalb eines Tages. Ob daraus Anfragen werden, zeigt sich meist in der ersten bis zweiten Woche. Die ersten Wochen sind allerdings Lernphase – das Konto sammelt Daten, und die Kosten pro Anfrage sinken danach typischerweise deutlich." },
      { q: "Was kostet die Betreuung?", a: "Ein fester, transparenter Betrag, unabhängig von Ihrem Werbebudget. Kein prozentualer Aufschlag, der mitwächst, wenn Sie mehr investieren – das setzt aus unserer Sicht falsche Anreize." },
      { q: "Können wir jederzeit aufhören?", a: "Ja. Das Konto gehört Ihnen, die Historie bleibt erhalten, und Sie können jederzeit pausieren oder wechseln. Wir betrachten es als unsere Aufgabe, Ihnen keinen Grund zum Wechseln zu geben." },
    ],
    belege: ["elektrobuga", "umzug-alpenland", "onyx-cuts"],
    example: {
      title: "Volle Auftragsbücher zur richtigen Zeit",
      persona: "Saisonaler Dienstleister",
      problem: "In der Hochsaison fehlten Anfragen genau dann, wenn Kapazität da war – Empfehlungen allein reichten nicht.",
      solution: "Wir haben zielgerichtete Kampagnen für die profitabelsten Leistungen aufgesetzt, mit eigenen Landingpages und sauberem Tracking.",
      result: "Planbare Anfragen zur richtigen Zeit und ein klares Bild, welcher Euro Werbebudget wie viel Umsatz bringt.",
    },
  },
  {
    slug: "qualitaetssicherung-qa",
    icon: "shield-check",
    title: "Qualitätssicherung & QA",
    navTitle: "QA & Testing",
    tagline: "Software, die im entscheidenden Moment nicht ausfällt.",
    summary:
      "Manuelles & automatisiertes Testing, Testmanagement und QA-Consulting auf Enterprise-Niveau – damit Ihre Software stabil, sicher und zuverlässig läuft.",
    keywords: [
      "Qualitätssicherung Software",
      "Testautomatisierung Wien",
      "QA Consulting Österreich",
      "Softwaretest Dienstleister",
      "automatisierte Softwaretests",
    ],
    metaTitle: "Qualitätssicherung & Testautomatisierung",
    metaDescription:
      "QA und Testautomatisierung aus Wien auf Enterprise-Niveau: Teststrategie, automatisierte Tests, Audits. Jetzt kostenloses Erstgespräch.",
    heroHeadline: "Software, die im entscheidenden Moment nicht ausfällt",
    heroSub:
      "Mit über 15 Jahren Erfahrung aus internationalen Enterprise-Projekten sorgen wir dafür, dass Ihre Software stabil, sicher und zuverlässig läuft – durch strukturiertes Testing und Automatisierung.",
    benefits: [
      { title: "Enterprise-Erfahrung", text: "Testmethoden aus internationalen Großprojekten – jetzt auch für Ihr Vorhaben verfügbar." },
      { title: "Automatisiert & effizient", text: "Automatisierte Tests fangen Fehler ab, bevor Ihre Kunden sie bemerken – bei jedem Release." },
      { title: "Von Anfang an eingebaut", text: "Qualität ist bei uns kein Nachgedanke, sondern Teil jeder Zeile Code." },
    ],
    visual: {
      kind: "matrix",
      caption: "Was sich zu testen lohnt – und was nicht",
      good: {
        title: "Testen lohnt sich",
        items: [
          "Abläufe, an denen Geld hängt: Bestellung, Zahlung, Anmeldung",
          "Berechnungen mit rechtlicher Wirkung",
          "Alles, was nach jeder Änderung erneut funktionieren muss",
          "Schnittstellen zu fremden Systemen",
          "Fehlerfälle – nicht nur der Weg, auf dem alles gutgeht",
        ],
      },
      bad: {
        title: "Lohnt selten",
        items: [
          "Gestaltung und Platzierung von Elementen",
          "Einmalige Auswertungen",
          "Code, der in Kürze ersetzt wird",
          "Fremdbibliotheken, die ihre eigenen Tests mitbringen",
        ],
      },
      note: "Vollständige Testabdeckung ist kein Ziel, sondern ein teures Missverständnis. Getestet wird, wo ein Fehler wehtut.",
    },
    deliverables: [
      "Manuelles & automatisiertes Testing",
      "Testmanagement & Teststrategie",
      "Aufbau nachhaltiger QA-Prozesse",
      "Tool-Einführung & Team-Schulung",
      "Qualitäts-Audits bestehender Software",
    ],
    sections: [
      {
        heading: "Was schlechte Qualität wirklich kostet",
        body:
          "Ein Fehler, der erst beim Kunden auffällt, kostet ein Vielfaches von dem, was seine frühe Entdeckung gekostet hätte – an Zeit, Geld und Vertrauen. Strukturierte Qualitätssicherung senkt dieses Risiko drastisch. Sie sorgt dafür, dass Ihre Software auch dann funktioniert, wenn es darauf ankommt: beim Kunden, unter Last, im Live-Betrieb.",
      },
      {
        heading: "Testautomatisierung, die sich rechnet",
        body:
          "Automatisierte Tests prüfen bei jeder Änderung in Sekunden, ob noch alles funktioniert. Das gibt Ihrem Team die Sicherheit, schnell und ohne Angst weiterzuentwickeln. Wir bauen die Automatisierung dort auf, wo sie den größten Nutzen bringt, und schulen Ihr Team, sie selbst zu pflegen.",
      },
      {
        heading: "QA-Consulting: nachhaltige Prozesse",
        body:
          "Wir liefern nicht nur Tests, sondern helfen Ihnen, Qualität dauerhaft zu verankern: passende Prozesse, die richtigen Werkzeuge, klare Verantwortlichkeiten und Schulungen. So bleibt die Qualität hoch, auch wenn wir längst nicht mehr im Projekt sind.",
      },
      {
        heading: "Welche Tests sich wann lohnen",
        body:
          "Nicht jeder Teil einer Anwendung verdient dieselbe Aufmerksamkeit. Wir setzen dort an, wo ein Fehler am teuersten wäre: bei Abläufen, die Geld bewegen, bei allem rund um Anmeldung und Berechtigungen, und bei Funktionen, die täglich von vielen Menschen benutzt werden. Für diese Bereiche lohnt sich Automatisierung, weil die Tests bei jeder Änderung wieder laufen. Selten genutzte Verwaltungsfunktionen prüft man dagegen günstiger von Hand – automatisierte Tests dafür zu schreiben und dauerhaft zu pflegen kostet mehr, als sie einbringen. Diese Abwägung treffen wir gemeinsam mit Ihnen, statt pauschal eine Abdeckungsquote anzustreben, die gut klingt und wenig aussagt.",
      },
      {
        heading: "Wie ein Qualitäts-Audit abläuft",
        body:
          "Wir sehen uns bestehende Software aus drei Richtungen an. Fachlich: Tut sie, was sie tun soll, und was passiert bei ungewöhnlichen Eingaben? Technisch: Wie ist der Code strukturiert, gibt es Tests, wie lange dauert ein Release, wie schnell fällt ein Fehler auf? Betrieblich: Was passiert bei Ausfall, gibt es Sicherungen, wurden sie je zurückgespielt? Das Ergebnis ist eine nach Risiko geordnete Liste mit Aufwandsschätzung – von Punkten, die kurzfristig gefährlich sind, bis zu solchen, die man im Blick behalten sollte. Ein Audit dauert typischerweise wenige Tage und ist oft der günstigste Weg, um vor einer größeren Investition Klarheit zu bekommen.",
      },
      {
        heading: "Was Testautomatisierung kostet und wann sie sich rechnet",
        body:
          "Automatisierte Tests kosten beim Aufbau Zeit und danach Pflege – das wird gerne verschwiegen. Sie rechnen sich, sobald dieselben Abläufe wiederholt geprüft werden müssen, also bei jeder Software, die weiterentwickelt wird. Die Rechnung ist einfach: Was kostet ein manueller Durchlauf aller kritischen Abläufe, wie oft im Jahr fällt er an, und wie hoch wäre der Schaden eines Fehlers, der zum Kunden durchrutscht. Bei Software, die einmal gebaut und nie geändert wird, lohnt sich Automatisierung nicht. Bei allem anderen ist die Frage nicht ob, sondern in welchem Umfang.",
      },
      {
        heading: "Was 15 Jahre Enterprise-Erfahrung praktisch bedeuten",
        body:
          "In internationalen Großprojekten ist Qualitätssicherung kein Anhängsel, sondern eine eigene Disziplin mit klaren Verfahren: Teststrategie vor Testfall, Nachvollziehbarkeit zwischen Anforderung und Prüfung, definierte Freigabekriterien, dokumentierte Abnahme. Diese Methoden sind für kleinere Vorhaben oft zu schwer – aber die dahinterliegenden Prinzipien nicht. Wir übertragen sie in einem Umfang, der zu Ihrem Projekt passt: genug Struktur, damit nichts durchrutscht, wenig genug, damit die Entwicklung nicht erstickt. Das ist der eigentliche Unterschied zwischen jemandem, der Tests schreibt, und jemandem, der Qualität verantwortet.",
      },
    ],
    faq: [
      { q: "Lohnt sich Testautomatisierung für kleine Projekte?", a: "Oft ja – zumindest für die kritischen Abläufe. Wir automatisieren gezielt dort, wo der Nutzen den Aufwand klar übersteigt." },
      { q: "Können Sie bestehende Software prüfen?", a: "Ja. In einem Qualitäts-Audit finden wir Schwachstellen, Risiken und die wichtigsten Ansatzpunkte für Verbesserungen." },
      { q: "Wir haben gar keine Tests – wo fangen wir an?", a: "Bei dem Ablauf, dessen Ausfall Sie am meisten schmerzen würde. Meist ist das die Strecke, an der Geld hängt. Ein einzelner automatisierter Test für diesen Weg bringt mehr Sicherheit als fünfzig Tests für Randfunktionen." },
      { q: "Wie lange dauert ein Qualitäts-Audit?", a: "Typischerweise wenige Tage, abhängig von Umfang und Alter der Software. Sie bekommen danach eine nach Risiko geordnete Liste mit Aufwandsschätzung – oft der günstigste Weg, vor einer größeren Investition Klarheit zu bekommen." },
      { q: "Können Sie unser Team schulen, statt alles selbst zu machen?", a: "Ja, und das ist häufig die bessere Investition. Wir bauen die Grundlage auf, führen die Werkzeuge ein und übergeben so, dass Ihr Team selbst weiterarbeiten kann. Dauerhafte Abhängigkeit ist bei Qualitätssicherung besonders unsinnig." },
      { q: "Testen Sie auch Software, die jemand anderes gebaut hat?", a: "Ja. Wir prüfen unabhängig davon, wer entwickelt hat. Bei laufenden Projekten mit externem Dienstleister ist eine unabhängige Prüfung oft besonders wertvoll – dann bewertet nicht derselbe die Qualität, der sie auch verantwortet." },
    ],
    example: {
      title: "Releases ohne Angst vor dem roten Telefon",
      persona: "Software-Team eines Mittelständlers",
      problem: "Jedes Update war ein Risiko: Nach dem Release klingelten die Telefone, weil an anderer Stelle etwas kaputtging.",
      solution: "Wir haben eine automatisierte Testabdeckung für die kritischen Abläufe aufgebaut und einen klaren QA-Prozess eingeführt.",
      result: "Releases, die durchlaufen, ohne dass etwas Unerwartetes bricht – und ein Team, das sich wieder auf neue Funktionen konzentriert.",
    },
  },
];

export function getService(slug: string): Service | undefined {
  return services.find((s) => s.slug === slug);
}

/**
 * EINE Logoleiste – nur Einträge mit echter Logodatei.
 *
 * Vorher standen hier zwei Reihen: Kunden oben, Konzernprojekte unten.
 * Das war zwar genau, sah aber doppelt aus, und die Kundenreihe bestand
 * überwiegend aus Namen in Schrift statt aus Logos – neben echten Logos
 * wirkt das wie ein Platzhalter. Özgür hat sich am 05.11.2026 für eine
 * Reihe mit Logos entschieden.
 *
 * WICHTIG ZUR BESCHRIFTUNG: Die Liste mischt zwei Dinge. Buga und Alpen
 * sind Kunden der WeDev-IT GmbH; MAGNA, KNAPP, MFL, Scientific Games und
 * STI Consulting sind Projekte aus Özgürs Zeit vor der Gründung. Die
 * Überschrift muss deshalb beides nennen. Stünde dort „unsere Kunden“,
 * wäre das irreführend (UWG) – genau deshalb standen hier vorher zwei
 * Reihen.
 *
 * Neue Einträge nur mit Logodatei. Ein Name in Schrift fällt zwischen
 * Logos sofort auf, und zwar unangenehm.
 */
export type Reference = { name: string; src: string; width: number; height: number };

export const logoLeiste: Reference[] = [
  { name: "MAGNA", src: "/references/magna.png", width: 1024, height: 240 },
  { name: "KNAPP", src: "/references/knapp.png", width: 1024, height: 326 },
  { name: "MFL", src: "/references/mfl.png", width: 1024, height: 245 },
  { name: "Scientific Games International", src: "/references/scientific-games.png", width: 599, height: 333 },
  { name: "STI Consulting", src: "/references/sti-consulting.svg", width: 289, height: 31 },
  { name: "Buga Elektroinstallation", src: "/references/elektro-buga.png", width: 1024, height: 287 },
  { name: "Umzug Alpenland", src: "/references/alpen.webp", width: 1024, height: 367 },
];

/** Beschriftung – nennt beide Gruppen, ohne eine davon zu behaupten. */
export const logoLeisteTitel =
  "Kunden der WeDev-IT GmbH und Konzernprojekte aus über 15 Jahren";

/**
 * Weitere referenzierte Kunden ohne Logo.
 * Aktuell nicht auf der Website ausgegeben – die Referenz-Sektion nennt
 * stattdessen eine Sammelangabe. Liste bleibt als Notiz erhalten.
 */
export const additionalClients = ["Smart and Repair", "iFix", "Idealx"];

/** Ablauf / Prozess-Schritte. */
export const processSteps = [
  {
    title: "Erstgespräch",
    text: "Wir hören zu und verstehen Ihr Ziel, Ihre Prozesse und Ihre Herausforderungen – kostenlos und unverbindlich.",
    icon: "phone",
    duration: "60 Minuten",
    outcome: "Ehrliche Einschätzung",
  },
  {
    title: "Strategie & Konzept",
    text: "Wir entwerfen die passende Lösung: technische Architektur, Umfang und einen klaren Fahrplan mit Fixpreis-Rahmen.",
    icon: "compass",
    duration: "1–2 Wochen",
    outcome: "Fixpreis-Rahmen",
  },
  {
    title: "Entwicklung & QA",
    text: "Wir setzen um – in transparenten Etappen, mit eingebauter Qualitätssicherung und laufendem Feedback.",
    icon: "code",
    duration: "laufend",
    outcome: "Nutzbare Etappen",
  },
  {
    title: "Go-Live & Support",
    text: "Wir bringen Ihre Lösung live, überwachen sie und entwickeln sie mit Ihnen weiter.",
    icon: "shield-check",
    duration: "dauerhaft",
    outcome: "Livebetrieb & Wartung",
  },
] as const;

/** Startseiten-FAQ. */
export const homeFaq = [
  {
    q: "Wie könnt ihr so viel günstiger sein als andere?",
    a: "Wir arbeiten in jedem Schritt KI-gestützt – bei Konzeption, Code, Tests und Dokumentation. Dadurch liefern wir in einem Bruchteil der Zeit, ohne bei der Qualität nachzulassen. Diese Ersparnis geben wir direkt an Sie weiter, oft bis zu 70 % günstiger als klassische Agenturen.",
  },
  {
    q: "Für welche Unternehmen arbeitet ihr?",
    a: "Für KMU, Startups und mittelständische Unternehmen im gesamten DACH-Raum, die Wert auf saubere Umsetzung, klare Prozesse und Transparenz legen. Vom Ein-Personen-Betrieb bis zum internationalen Konzern.",
  },
  {
    q: "Übernehmt ihr komplette Projekte oder auch einzelne Aufgaben?",
    a: "Beides. Wir setzen ganze Projekte von der Idee bis zum Go-Live um oder unterstützen punktuell dort, wo Sie gerade Kapazität oder Expertise brauchen.",
  },
  {
    q: "Was kostet ein Projekt bei WeDev-IT?",
    a: "Das hängt von Umfang und Komplexität ab. Wir berücksichtigen Ihre Unternehmensgröße und prüfen mögliche Förderungen. Im kostenlosen Erstgespräch bekommen Sie eine ehrliche Einschätzung und einen klaren Kostenrahmen.",
  },
  {
    q: "Wie stellt ihr Qualität und Sicherheit sicher?",
    a: "Durch manuelles und automatisiertes Testing, strukturiertes Testmanagement und saubere technische Architektur. Mit über 15 Jahren Erfahrung aus Enterprise-Projekten ist Qualitätssicherung fest in unserem Vorgehen verankert.",
  },
];
